import { useCallback, useEffect, useId, useRef, useState } from "react";
import { publicApi } from "./api";
import {
  conversation,
  conversationViews,
  getConversationView,
  getQuestionView,
  getReflectionView,
  getStoryView,
  reflectionViews,
  questionViews,
  storyViews,
  upcomingConversationPreviews,
  type ConversationView,
  type QuestionView,
  type ReflectionView,
  type StoryView,
} from "./content";
import type {
  Experience,
  IdentityMode,
  Language,
  QuestionVoice,
} from "./types";
import type { ResponsiveImage } from "./content-model";

const CONSENT_VERSION = "2026-08-13";
const QUESTION_REUSE_CONSENT_VERSION = "2026-08-25";
const conversationPath = `/conversations/${conversation.slug}/`;
const publicApiEnabled = Boolean(import.meta.env.VITE_API_URL);
const newsletterEnabled =
  publicApiEnabled && import.meta.env.VITE_NEWSLETTER_ENABLED === "true";

function EditorialImage({
  image,
  alt,
  sizes,
  priority = false,
}: {
  image: ResponsiveImage;
  alt: string;
  sizes: string;
  priority?: boolean;
}) {
  const fallback = image.variants[image.variants.length - 1];
  return (
    <img
      src={fallback.src}
      srcSet={image.variants
        .map((variant) => `${variant.src} ${variant.width}w`)
        .join(", ")}
      sizes={sizes}
      width={image.width}
      height={image.height}
      alt={alt}
      decoding="async"
      loading={priority ? "eager" : "lazy"}
      fetchPriority={priority ? "high" : "auto"}
    />
  );
}

const ui = {
  en: {
    skip: "Skip to content",
    homeAria: "HeartSpeaks home",
    languageAria: "Content language",
    brandTagline: "One question.\nMany lives.",
    nav: ["Conversations", "Stories", "Reflections", "About"],
    primaryNav: "Primary navigation",
    share: "Share your experience",
    headerRead: "Read the conversation",
    weekly: "Latest question",
    read: "Read the conversation",
    private: "Reflect privately",
    readTime: "min read",
    perspectiveCount: "perspectives",
    bilingual: "English & Tamil",
    homeTrustLine: "Real experiences · Thoughtful writing · English + தமிழ்",
    ritual: "One question. Many lives.",
    ritualBody:
      "A simple editorial path from a meaningful question to experiences worth preserving.",
    steps: [
      { title: "Read", body: "A carefully explored human question." },
      { title: "Listen", body: "Discover how different people experience it." },
      { title: "Share", body: "Tell us what it looked like in your own life." },
    ],
    returning: "Welcome back to your weekly pause",
    reasonReturn: "How HeartSpeaks works",
    heroOverlay: "perspectives · 1 question",
    fromConversation: "From the conversation",
    editorialComposite: "Editorial composite",
    questionLed: "Question-led discovery",
    openConversation: "Open conversation →",
    comingNext: "Coming next",
    comingBody: "One new question will continue this quiet weekly ritual.",
    voicesKicker: "Experience over opinion",
    topics: "Explore a part of life",
    topicsBody: "Follow the question that feels closest to you today.",
    voices: "Three lives. Three meanings of happiness.",
    voicesBody:
      "Editorial composites show the range this question can hold. Genuine reader stories are always labelled separately.",
    readPerspectives: "Read all perspectives",
    moreQuestions: "Questions worth sitting with",
    moreQuestionsBody:
      "These conversations are being prepared with the same editorial care.",
    comingSoon: "Coming soon",
    aboutKicker: "What is HeartSpeaks",
    aboutTitle: "Ordinary life, examined with care.",
    aboutBody:
      "HeartSpeaks is a bilingual publication about the parts of life we rarely put into words. We take one human question at a time and stay with it — in English and Tamil, without advice, rankings, or a single correct answer.",
    pillars: [
      {
        title: "Conversations",
        body: "One human question, explored through an original reflection and several clearly differentiated perspectives.",
        href: "/conversations/",
        cta: "All conversations",
      },
      {
        title: "Stories",
        body: "One life at a time — a person's circumstances, choices, turning point, and what they understand now.",
        href: "/stories/",
        cta: "All stories",
      },
      {
        title: "Reflections",
        body: "An idea encountered in a book, film, or ordinary observation, returned to you as a question worth carrying.",
        href: "/reflections/",
        cta: "All reflections",
      },
    ],
    latestConversations: "Latest conversations",
    latestConversationsBody:
      "Each one begins with a question people actually ask themselves, and makes room for more than one honest answer.",
    latestReflections: "Latest reflections",
    latestReflectionsBody:
      "Ideas we encountered elsewhere, followed until they said something about ordinary living.",
    viewAll: "View all",
    exploreNav: "Explore",
    search: "Search",
    searchAria: "Search HeartSpeaks",
    searchPlaceholder: "Search questions, family, happiness…",
    homeHeroTitle: "A place for the things we rarely say aloud.",
    homeHeroBody:
      "HeartSpeaks is a space for honest conversations, lived experiences, and reflections about the parts of life we don’t always know how to talk about.",
    homePrimaryCta: "Start exploring",
    homeSecondaryCta: "Why we created HeartSpeaks",
    homePanelKicker: "The things we carry",
    homePanelQuestions: [
      "Did I understand my parents too late?",
      "Why can a successful life still feel incomplete?",
      "When does protecting become controlling?",
    ],
    homePanelTypes: "Questions without easy answers.",
    homeAboutTitle: "What is HeartSpeaks?",
    homeAboutStatement: [
      "Some parts of life don’t fit into advice, news, or social media posts.",
      "They live somewhere between experience and reflection—the things we understand only after living through them.",
      "HeartSpeaks is a place for those conversations.",
    ],
    homeTopicsTitle: "What we talk about",
    homeTopicsBody:
      "The ordinary subjects that shape a life, but rarely fit into a simple answer.",
    homeTopics: [
      "Family",
      "Relationships",
      "Growing older",
      "Happiness",
      "Choices",
      "Regret",
      "Identity",
      "Work & ambition",
      "Parenthood",
      "The past",
    ],
    homeConversationsTitle: "Questions worth sitting with.",
    homeConversationsBody:
      "Different perspectives around questions that rarely have one correct answer.",
    homeStoriesTitle: "Lives behind the questions.",
    homeStoriesBody:
      "Real experiences, carefully told—with their circumstances, turning points, and unresolved edges intact.",
    homeReflectionsTitle:
      "Sometimes an experience needs time before it becomes an understanding.",
    homeReflectionsBody:
      "Ideas and observations followed slowly enough to reveal something about ordinary living.",
    homeBilingualTitle:
      "Some thoughts feel different in the language we grew up with.",
    homeBilingualBody: "Read HeartSpeaks in English and Tamil.",
    readEnglish: "Read in English",
    readTamil: "தமிழில் வாசிக்க",
    browseAll: "Browse the complete library",
    exploreKicker: "Browse the library",
    exploreTitle: "Find a question that stays with you.",
    exploreBody:
      "Search the full HeartSpeaks collection or filter it by the kind of reading you need today.",
    allContent: "All",
    clearSearch: "Clear search",
    resetLibrary: "Show all content",
    searchResults: "Library results",
    noResultsTitle: "No results found.",
    noResultsBody:
      "Try a shorter phrase, another topic, or clear the filters to browse everything.",
    result: "result",
    results: "results",
    conversationType: "Conversation",
    storyType: "Story",
    reflectionType: "Reflection",
    publishedLabel: "Published",
    readingLabel: "Read",
    editorialImage: "Editorial image",
    picturedNotContributors: "The people pictured are not contributors.",
    storiesKicker: "Stories from ordinary lives",
    storiesTitle: "One life, told with care.",
    storiesBody:
      "Stories stay close to one person’s circumstances, turning point, and what they understand now.",
    browseStories: "Browse stories",
    noStoriesTitle: "The first stories are being prepared.",
    noStoriesBody:
      "We’re carefully editing our first reader and interview stories. Every published story will include clear sourcing, human review, and appropriate consent.",
    howStoriesPublished: "How stories are published",
    storyTypeReader: "A HeartSpeaks reader story",
    storyTypeInterview: "A HeartSpeaks interview",
    storyTypeEditorial: "Editorial story",
    relatedConversation: "Part of the conversation",
    storySource: "Source and editorial note",
    subscribeTitle: "One good question. Once a week.",
    subscribeBody:
      "Receive the Question of the Week and a small selection of newly published perspectives. No engagement spam.",
    email: "Your email address",
    subscribe: "Send me the weekly question",
    subscribeConsent:
      "I agree to receive the HeartSpeaks weekly question and can unsubscribe at any time.",
    subscribed: "Check your inbox to confirm your subscription.",
    localSubscribe:
      "Your preference was saved, but email delivery must be configured before confirmation can be sent.",
    reflectionTitle: "Keep this answer just for you",
    reflectionBody:
      "This draft stays in this browser. HeartSpeaks does not receive it unless you deliberately continue to the sharing form.",
    reflectionPlaceholder: "My honest answer today is…",
    savePrivate: "Save privately on this device",
    savedPrivate: "Saved on this device",
    continueShare: "I want to share this with HeartSpeaks",
    conversationLabel: "HeartSpeaks conversation",
    questionsWorth: "Questions worth sitting with",
    compositesLabel: "Editorial composites",
    communityLabel: "Published with permission",
    startReflection: "Start with a private answer",
    deeper: "Read the full reflection",
    next: "What will you answer next?",
    nextBody:
      "A new question creates a reason to return without creating another noisy feed.",
    footer: "A place for the things we rarely say aloud.",
    explore: "Explore",
    trustHeading: "Trust",
    removal: "Request story removal",
    editorSignIn: "Editor sign in",
    privacy: "Privacy",
    guidelines: "Editorial guidelines",
    contact: "Contact",
    editorialPromise: "Our editorial promise",
    editorialPromiseBody:
      "Original reflections. Human moderation. Publication only with consent.",
    privateSeal: "Private",
    privateOnly: "Only on this device",
    newsletterKicker: "Question of the Week",
    newsletterError: "We could not save this right now. Please try again.",
    loadingExperiences: "Loading reader experiences…",
    editorialReflection: "Editorial reflection",
    originalEditorial: "Original HeartSpeaks editorial",
    minRead: "min read",
    compositeDisclaimer:
      "Illustrative editorial composites · not individual testimonies",
    sharePrinciple:
      "Your experience does not need to be extraordinary to matter.",
    sharePrincipleLabel: "HeartSpeaks editorial principle",
    shareTrust: [
      "Share privately or anonymously",
      "Reviewed by a human editor",
      "Published only with consent",
    ],
    returnDashboard: "Return to this week’s question →",
    backThisWeek: "Latest at HeartSpeaks",
    notFoundTitle: "This conversation is not available.",
    notFoundBody:
      "It may still be an editorial draft, or the address may be incorrect.",
    notFoundReturn: "Return to this week’s question",
    storyNotFoundTitle: "This story is not available.",
    storyNotFoundBody:
      "It may still be under editorial review, or the address may be incorrect.",
    storyNotFoundReturn: "Browse stories",
    reflectionNotFoundTitle: "This reflection is not available.",
    reflectionNotFoundBody:
      "It may still be awaiting publication, or the address may be incorrect.",
    reflectionNotFoundReturn: "Browse reflections",
    done: "Done",
    thanks: "Thank you for trusting this space.",
    shareArticle: "Share",
    shareArticleAria: "Share this article",
    sharePanelTitle: "Share this article",
    shareAgain: "Pass this along",
    shareWhatsApp: "WhatsApp",
    shareLinkedIn: "LinkedIn",
    shareX: "X",
    copyLink: "Copy link",
    linkCopied: "Link copied",
    copyLinkFailed: "We couldn't copy the link. Please try again.",
    readOn: "Read on HeartSpeaks",
    shareKicker: "Share only if you want to",
    preview: "Preview",
    edit: "Edit",
    sending: "Sending…",
    anonymous: "Anonymous",
    anonymousHelp: "Published without your name",
    firstName: "First name",
    firstNameHelp: "Only your first name is shown",
    privateIdentity: "Private",
    privateIdentityHelp: "Never available for publication",
    ageRange: "Age range",
    optional: "Optional",
    preferNot: "Prefer not to say",
    piiWarning:
      "Please remove phone numbers and email addresses. An editor will also check for identifying details.",
    previewResponse: "Preview your response",
    submissionError: "We could not save this just now. Please try again.",
  },
  ta: {
    skip: "உள்ளடக்கத்திற்குச் செல்லவும்",
    homeAria: "HeartSpeaks முகப்பு",
    languageAria: "உள்ளடக்க மொழி",
    brandTagline: "ஒரு கேள்வி.\nபல வாழ்க்கைகள்.",
    nav: ["உரையாடல்கள்", "கதைகள்", "சிந்தனைகள்", "எங்களைப் பற்றி"],
    primaryNav: "முதன்மை வழிசெலுத்தல்",
    share: "உங்கள் அனுபவத்தைப் பகிரவும்",
    headerRead: "உரையாடலை வாசிக்கவும்",
    weekly: "சமீபத்திய கேள்வி",
    read: "உரையாடலை வாசிக்கவும்",
    private: "தனிப்பட்ட முறையில் சிந்திக்கவும்",
    readTime: "நிமிட வாசிப்பு",
    perspectiveCount: "கண்ணோட்டங்கள்",
    bilingual: "தமிழ் & English",
    homeTrustLine: "உண்மையான அனுபவங்கள் · கவனமான எழுத்து · English + தமிழ்",
    ritual: "ஒரு கேள்வி. பல வாழ்க்கைகள்.",
    ritualBody:
      "ஒரு அர்த்தமுள்ள கேள்வியிலிருந்து பாதுகாக்க வேண்டிய அனுபவங்கள் வரை செல்லும் எளிய ஆசிரியர் பாதை.",
    steps: [
      { title: "வாசியுங்கள்", body: "கவனமாக ஆராயப்பட்ட ஒரு மனிதக் கேள்வி." },
      {
        title: "கேளுங்கள்",
        body: "வெவ்வேறு மனிதர்கள் அதை எப்படி வாழ்கிறார்கள் என்பதை அறியுங்கள்.",
      },
      {
        title: "பகிருங்கள்",
        body: "அது உங்கள் வாழ்க்கையில் எப்படி இருந்தது என்பதைச் சொல்லுங்கள்.",
      },
    ],
    returning: "உங்கள் வாராந்திர இடைவெளிக்கு மீண்டும் வரவேற்கிறோம்",
    reasonReturn: "HeartSpeaks செயல்படும் விதம்",
    heroOverlay: "கண்ணோட்டங்கள் · 1 கேள்வி",
    fromConversation: "உரையாடலிலிருந்து",
    editorialComposite: "ஆசிரியர் தொகுப்பு",
    questionLed: "கேள்வி வழி தேடல்",
    openConversation: "உரையாடலைத் திறக்கவும் →",
    comingNext: "அடுத்து வருகிறது",
    comingBody: "ஒரு புதிய கேள்வி இந்த அமைதியான வாராந்திர பழக்கத்தைத் தொடரும்.",
    voicesKicker: "கருத்தை விட அனுபவம்",
    topics: "வாழ்க்கையின் ஒரு பகுதியை ஆராயுங்கள்",
    topicsBody: "இன்று உங்களுக்கு நெருக்கமாக இருக்கும் கேள்வியைத் தொடருங்கள்.",
    voices: "மூன்று வாழ்க்கைகள். மகிழ்ச்சிக்கு மூன்று அர்த்தங்கள்.",
    voicesBody:
      "இந்தக் கேள்வி தாங்கக்கூடிய பரப்பை ஆசிரியர் தொகுப்புகள் காட்டுகின்றன. உண்மையான வாசகர் கதைகள் எப்போதும் தனியாகக் குறிக்கப்படும்.",
    readPerspectives: "அனைத்து கண்ணோட்டங்களையும் வாசிக்கவும்",
    moreQuestions: "நிதானமாகச் சிந்திக்க வேண்டிய கேள்விகள்",
    moreQuestionsBody: "இந்த உரையாடல்கள் அதே ஆசிரியர் கவனத்துடன் தயாராகின்றன.",
    comingSoon: "விரைவில்",
    aboutKicker: "HeartSpeaks என்றால் என்ன",
    aboutTitle: "சாதாரண வாழ்க்கை, கவனத்தோடு.",
    aboutBody:
      "வார்த்தைகளில் நாம் அரிதாகவே சொல்லும் வாழ்க்கைப் பகுதிகளைப் பற்றிய இருமொழி வெளியீடு HeartSpeaks. ஒரு நேரத்தில் ஒரு மனிதக் கேள்வியை எடுத்து, அதனோடு நிதானமாக இருக்கிறோம் — தமிழிலும் ஆங்கிலத்திலும், அறிவுரைகள் இல்லாமல், சரியான பதில் ஒன்றுதான் என்று சொல்லாமல்.",
    pillars: [
      {
        title: "உரையாடல்கள்",
        body: "ஒரு மனிதக் கேள்வி. அதற்கான சொந்தச் சிந்தனையும், தெளிவாக வேறுபடும் பல கண்ணோட்டங்களும்.",
        href: "/conversations/",
        cta: "எல்லா உரையாடல்களும்",
      },
      {
        title: "கதைகள்",
        body: "ஒரு நேரத்தில் ஒரு வாழ்க்கை — சூழ்நிலை, தேர்வுகள், திருப்புமுனை, இப்போது அவர்களுக்குப் புரிவது.",
        href: "/stories/",
        cta: "எல்லாக் கதைகளும்",
      },
      {
        title: "சிந்தனைகள்",
        body: "புத்தகத்திலோ, திரைப்படத்திலோ, அன்றாட அவதானிப்பிலோ கிடைத்த ஒரு எண்ணம் — உங்களுக்கான கேள்வியாகத் திரும்பி வருகிறது.",
        href: "/reflections/",
        cta: "எல்லாச் சிந்தனைகளும்",
      },
    ],
    latestConversations: "சமீபத்திய உரையாடல்கள்",
    latestConversationsBody:
      "ஒவ்வொன்றும் மக்கள் தங்களுக்குள்ளேயே கேட்கும் ஒரு கேள்வியில் தொடங்குகிறது; ஒன்றுக்கு மேற்பட்ட நேர்மையான பதில்களுக்கு இடம் தருகிறது.",
    latestReflections: "சமீபத்திய சிந்தனைகள்",
    latestReflectionsBody:
      "வேறு எங்கோ சந்தித்த எண்ணங்கள் — சாதாரண வாழ்க்கையைப் பற்றி அவை ஏதாவது சொல்லும் வரை பின்தொடர்ந்தவை.",
    viewAll: "அனைத்தையும் பார்க்க",
    exploreNav: "ஆராயுங்கள்",
    search: "தேடுங்கள்",
    searchAria: "HeartSpeaks-ல் தேடுங்கள்",
    searchPlaceholder: "கேள்விகள், குடும்பம், மகிழ்ச்சி…",
    homeHeroTitle: "நாம் அரிதாகவே உரக்கச் சொல்லும் விஷயங்களுக்கான ஓர் இடம்.",
    homeHeroBody:
      "வாழ்க்கையின் சில பகுதிகளை எப்படிப் பேசுவது என்று எப்போதும் நமக்குத் தெரிவதில்லை. அவற்றைப் பற்றிய நேர்மையான உரையாடல்கள், வாழ்ந்த அனுபவங்கள், சிந்தனைகளுக்கான இடம் HeartSpeaks.",
    homePrimaryCta: "ஆராயத் தொடங்குங்கள்",
    homeSecondaryCta: "HeartSpeaks-ஐ ஏன் உருவாக்கினோம்",
    homePanelKicker: "நாம் சுமக்கும் விஷயங்கள்",
    homePanelQuestions: [
      "என் பெற்றோரை நான் மிகவும் தாமதமாகப் புரிந்துகொண்டேனா?",
      "வெற்றிகரமான வாழ்க்கைகூட ஏன் நிறைவற்றதாகத் தோன்றுகிறது?",
      "பாதுகாப்பது எப்போது கட்டுப்படுத்துவதாக மாறுகிறது?",
    ],
    homePanelTypes: "எளிய பதில்கள் இல்லாத கேள்விகள்.",
    homeAboutTitle: "HeartSpeaks என்றால் என்ன?",
    homeAboutStatement: [
      "வாழ்க்கையின் சில பகுதிகள் அறிவுரை, செய்தி அல்லது சமூக ஊடகப் பதிவுகளுக்குள் அடங்குவதில்லை.",
      "அவை அனுபவத்திற்கும் சிந்தனைக்கும் இடையே வாழ்கின்றன—வாழ்ந்து கடந்த பிறகே நமக்குப் புரியும் விஷயங்களாக.",
      "அத்தகைய உரையாடல்களுக்கான இடம்தான் HeartSpeaks.",
    ],
    homeTopicsTitle: "நாம் பேசும் விஷயங்கள்",
    homeTopicsBody:
      "ஒரு வாழ்க்கையை வடிவமைக்கும், ஆனால் எளிய பதிலுக்குள் அடங்காத அன்றாட விஷயங்கள்.",
    homeTopics: [
      "குடும்பம்",
      "உறவுகள்",
      "வயதாகுதல்",
      "மகிழ்ச்சி",
      "தேர்வுகள்",
      "வருத்தம்",
      "அடையாளம்",
      "வேலையும் இலட்சியமும்",
      "பெற்றோராக இருப்பது",
      "கடந்த காலம்",
    ],
    homeConversationsTitle: "நிதானமாகச் சிந்திக்க வேண்டிய கேள்விகள்.",
    homeConversationsBody:
      "ஒரே சரியான பதில் இல்லாத கேள்விகளைச் சுற்றிய வெவ்வேறு கண்ணோட்டங்கள்.",
    homeStoriesTitle: "கேள்விகளுக்குப் பின்னால் உள்ள வாழ்க்கைகள்.",
    homeStoriesBody:
      "சூழ்நிலைகள், திருப்புமுனைகள், தீராத விளிம்புகள் ஆகியவற்றுடன் அக்கறையாகச் சொல்லப்படும் உண்மையான அனுபவங்கள்.",
    homeReflectionsTitle:
      "சில நேரங்களில் ஓர் அனுபவம் புரிதலாக மாற காலம் தேவைப்படுகிறது.",
    homeReflectionsBody:
      "சாதாரண வாழ்வைப் பற்றி ஏதோ ஒன்றை வெளிப்படுத்தும் வரை மெதுவாகத் தொடரப்படும் எண்ணங்களும் அவதானிப்புகளும்.",
    homeBilingualTitle:
      "நாம் வளர்ந்த மொழியில் சில எண்ணங்கள் வேறுவிதமாக உணரப்படுகின்றன.",
    homeBilingualBody: "HeartSpeaks-ஐ தமிழிலும் ஆங்கிலத்திலும் வாசியுங்கள்.",
    readEnglish: "Read in English",
    readTamil: "தமிழில் வாசிக்க",
    browseAll: "முழுத் தொகுப்பையும் பாருங்கள்",
    exploreKicker: "தொகுப்பை ஆராயுங்கள்",
    exploreTitle: "உங்களோடு நிலைக்கும் ஒரு கேள்வியைக் கண்டறியுங்கள்.",
    exploreBody:
      "முழு HeartSpeaks தொகுப்பிலும் தேடுங்கள் அல்லது இன்று நீங்கள் வாசிக்க விரும்பும் வகையைத் தேர்ந்தெடுங்கள்.",
    allContent: "அனைத்தும்",
    clearSearch: "தேடலை அழிக்கவும்",
    resetLibrary: "அனைத்து உள்ளடக்கத்தையும் பார்க்க",
    searchResults: "தொகுப்பு முடிவுகள்",
    noResultsTitle: "முடிவுகள் எதுவும் இல்லை.",
    noResultsBody:
      "குறுகிய சொற்றொடரை அல்லது வேறு தலைப்பை முயற்சிக்கவும்; இல்லையெனில் வடிகட்டிகளை அழிக்கவும்.",
    result: "முடிவு",
    results: "முடிவுகள்",
    conversationType: "உரையாடல்",
    storyType: "கதை",
    reflectionType: "சிந்தனை",
    publishedLabel: "வெளியிடப்பட்டது",
    readingLabel: "வாசிக்க",
    editorialImage: "ஆசிரியர் படம்",
    picturedNotContributors: "படத்தில் உள்ளவர்கள் பங்களிப்பாளர்கள் அல்ல.",
    storiesKicker: "சாதாரண வாழ்க்கைகளிலிருந்து கதைகள்",
    storiesTitle: "ஒரு வாழ்க்கை, அக்கறையுடன் சொல்லப்படுகிறது.",
    storiesBody:
      "ஒவ்வொரு கதையும் ஒருவரின் சூழல், திருப்புமுனை, இன்று அவர் புரிந்துகொள்வது ஆகியவற்றுடன் நெருக்கமாக நிற்கிறது.",
    browseStories: "கதைகளைப் பாருங்கள்",
    noStoriesTitle: "முதல் கதைகள் தயாராகின்றன.",
    noStoriesBody:
      "எங்கள் முதல் வாசகர் மற்றும் நேர்காணல் கதைகளை அக்கறையுடன் திருத்தி வருகிறோம். வெளியிடப்படும் ஒவ்வொரு கதையிலும் தெளிவான மூலம், மனிதப் பரிசீலனை, தேவையான அனுமதி ஆகியவை இருக்கும்.",
    howStoriesPublished: "கதைகள் எவ்வாறு வெளியிடப்படுகின்றன",
    storyTypeReader: "HeartSpeaks வாசகர் கதை",
    storyTypeInterview: "HeartSpeaks நேர்காணல்",
    storyTypeEditorial: "ஆசிரியர் கதை",
    relatedConversation: "இந்த உரையாடலின் ஒரு பகுதி",
    storySource: "ஆதாரமும் ஆசிரியர் குறிப்பும்",
    subscribeTitle: "ஒரு நல்ல கேள்வி. வாரம் ஒருமுறை.",
    subscribeBody:
      "வாரக் கேள்வியையும் புதிதாக வெளியிடப்பட்ட சில கண்ணோட்டங்களையும் பெறுங்கள். தேவையற்ற அறிவிப்புகள் இல்லை.",
    email: "உங்கள் மின்னஞ்சல் முகவரி",
    subscribe: "வாரக் கேள்வியை அனுப்பவும்",
    subscribeConsent:
      "HeartSpeaks வாரக் கேள்வியைப் பெற ஒப்புக்கொள்கிறேன்; எப்போது வேண்டுமானாலும் விலகலாம்.",
    subscribed: "உங்கள் சந்தாவை உறுதிப்படுத்த மின்னஞ்சலைச் சரிபார்க்கவும்.",
    localSubscribe:
      "உங்கள் விருப்பம் சேமிக்கப்பட்டது; உறுதிப்படுத்தல் அனுப்ப மின்னஞ்சல் சேவை கட்டமைக்கப்பட வேண்டும்.",
    reflectionTitle: "இந்தப் பதிலை உங்களுக்காக மட்டும் வைத்துக்கொள்ளுங்கள்",
    reflectionBody:
      "இந்த வரைவு இந்த உலாவியிலேயே இருக்கும். பகிரும் படிவத்திற்குச் செல்லும் வரை HeartSpeaks இதைப் பெறாது.",
    reflectionPlaceholder: "இன்றைய என் நேர்மையான பதில்…",
    savePrivate: "இந்தச் சாதனத்தில் தனிப்பட்ட முறையில் சேமிக்கவும்",
    savedPrivate: "இந்தச் சாதனத்தில் சேமிக்கப்பட்டது",
    continueShare: "இதை HeartSpeaks உடன் பகிர விரும்புகிறேன்",
    conversationLabel: "HeartSpeaks உரையாடல்",
    questionsWorth: "நிதானமாகச் சிந்திக்க வேண்டிய கேள்விகள்",
    compositesLabel: "ஆசிரியர் தொகுப்புகள்",
    communityLabel: "அனுமதியுடன் வெளியிடப்பட்டது",
    startReflection: "தனிப்பட்ட பதிலுடன் தொடங்குங்கள்",
    deeper: "முழு சிந்தனையையும் வாசிக்கவும்",
    next: "அடுத்து நீங்கள் எதற்குப் பதிலளிப்பீர்கள்?",
    nextBody:
      "புதிய கேள்வி மற்றொரு இரைச்சலான செய்தியோடை இல்லாமல் திரும்புவதற்கான காரணத்தை உருவாக்குகிறது.",
    footer: "நாம் உரக்கச் சொல்லாத விஷயங்களுக்கான ஓர் இடம்.",
    explore: "ஆராயுங்கள்",
    trustHeading: "நம்பிக்கை",
    removal: "கதையை நீக்கக் கோரவும்",
    editorSignIn: "ஆசிரியர் உள்நுழைவு",
    privacy: "தனியுரிமை",
    guidelines: "ஆசிரியர் வழிகாட்டுதல்கள்",
    contact: "தொடர்பு",
    editorialPromise: "எங்கள் ஆசிரியர் உறுதி",
    editorialPromiseBody:
      "அசல் சிந்தனைகள். மனித பரிசீலனை. அனுமதியுடன் மட்டுமே வெளியீடு.",
    privateSeal: "தனிப்பட்டது",
    privateOnly: "இந்தச் சாதனத்தில் மட்டும்",
    newsletterKicker: "வாரக் கேள்வி",
    newsletterError: "இப்போது சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
    loadingExperiences: "வாசகர் அனுபவங்கள் ஏற்றப்படுகின்றன…",
    editorialReflection: "ஆசிரியர் சிந்தனை",
    originalEditorial: "HeartSpeaks அசல் ஆசிரியர் பதிவு",
    minRead: "நிமிட வாசிப்பு",
    compositeDisclaimer:
      "விளக்கத்திற்கான ஆசிரியர் தொகுப்புகள் · தனிநபர் சாட்சியங்கள் அல்ல",
    sharePrinciple:
      "உங்கள் அனுபவம் முக்கியமாக இருக்க அசாதாரணமாக இருக்க வேண்டியதில்லை.",
    sharePrincipleLabel: "HeartSpeaks ஆசிரியர் கொள்கை",
    shareTrust: [
      "தனிப்பட்ட முறையிலோ பெயரில்லாமலோ பகிருங்கள்",
      "மனித ஆசிரியரால் பரிசீலிக்கப்படும்",
      "அனுமதியுடன் மட்டுமே வெளியிடப்படும்",
    ],
    returnDashboard: "சமீபத்திய கேள்விக்குத் திரும்பவும் →",
    backThisWeek: "HeartSpeaks சமீபத்தியவை",
    notFoundTitle: "இந்த உரையாடல் கிடைக்கவில்லை.",
    notFoundBody:
      "இது இன்னும் ஆசிரியர் வரைவாக இருக்கலாம் அல்லது முகவரி தவறாக இருக்கலாம்.",
    notFoundReturn: "சமீபத்திய கேள்விக்குத் திரும்பவும்",
    storyNotFoundTitle: "இந்தக் கதை கிடைக்கவில்லை.",
    storyNotFoundBody:
      "இது இன்னும் ஆசிரியர் பரிசீலனையில் இருக்கலாம் அல்லது முகவரி தவறாக இருக்கலாம்.",
    storyNotFoundReturn: "கதைகளைப் பாருங்கள்",
    reflectionNotFoundTitle: "இந்தச் சிந்தனை கிடைக்கவில்லை.",
    reflectionNotFoundBody:
      "இது இன்னும் வெளியீட்டிற்காகக் காத்திருக்கலாம் அல்லது முகவரி தவறாக இருக்கலாம்.",
    reflectionNotFoundReturn: "சிந்தனைகளைப் பாருங்கள்",
    done: "முடிந்தது",
    thanks: "இந்த இடத்தை நம்பியதற்கு நன்றி.",
    shareArticle: "பகிரவும்",
    shareArticleAria: "இந்தப் பதிவைப் பகிரவும்",
    sharePanelTitle: "இந்தப் பதிவைப் பகிரவும்",
    shareAgain: "இதையும் பகிரலாம்",
    shareWhatsApp: "WhatsApp",
    shareLinkedIn: "LinkedIn",
    shareX: "X",
    copyLink: "இணைப்பை நகலெடுக்கவும்",
    linkCopied: "இணைப்பு நகலெடுக்கப்பட்டது",
    copyLinkFailed: "இணைப்பை நகலெடுக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
    readOn: "HeartSpeaks-ல் வாசிக்கவும்",
    shareKicker: "விருப்பமிருந்தால் மட்டும் பகிருங்கள்",
    preview: "முன்னோட்டம்",
    edit: "திருத்தவும்",
    sending: "அனுப்பப்படுகிறது…",
    anonymous: "பெயரில்லாமல்",
    anonymousHelp: "உங்கள் பெயரின்றி வெளியிடப்படும்",
    firstName: "முதல் பெயர்",
    firstNameHelp: "உங்கள் முதல் பெயர் மட்டும் காட்டப்படும்",
    privateIdentity: "தனிப்பட்டது",
    privateIdentityHelp: "வெளியீட்டிற்கு ஒருபோதும் எடுத்துக்கொள்ளப்படாது",
    ageRange: "வயது வரம்பு",
    optional: "விருப்பத்தேர்வு",
    preferNot: "சொல்ல விரும்பவில்லை",
    piiWarning:
      "தொலைபேசி எண்கள் மற்றும் மின்னஞ்சல் முகவரிகளை நீக்கவும். அடையாள விவரங்களையும் ஆசிரியர் சரிபார்ப்பார்.",
    previewResponse: "உங்கள் பதிலை முன்னோட்டமிடவும்",
    submissionError: "இப்போது சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
  },
} as const;

function canonicalUrlForCurrentPage() {
  if (typeof window === "undefined") return "";
  const pathname = window.location.pathname.replace(/\/+$/, "") || "/";
  return `${window.location.origin}${pathname === "/" ? "/" : `${pathname}/`}`;
}

function isCompactShareDevice() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.("(max-width: 760px), (pointer: coarse)").matches
  );
}

async function copyCanonicalUrl(url: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return true;
  }

  const input = document.createElement("textarea");
  input.value = url;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.append(input);
  input.select();
  const copied = document.execCommand("copy");
  input.remove();
  return copied;
}

function ArticleShare({
  language,
  title,
  summary,
  published,
}: {
  language: Language;
  title: string;
  summary: string;
  published: boolean;
}) {
  const copy = ui[language];
  const [panelOpen, setPanelOpen] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">(
    "idle",
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);
  const panelId = useId();

  useEffect(() => {
    if (!panelOpen) {
      if (wasOpen.current) {
        triggerRef.current?.focus();
        wasOpen.current = false;
      }
      return;
    }

    wasOpen.current = true;
    panelRef.current?.querySelector<HTMLElement>("a, button")?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPanelOpen(false);
    };
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (
        !panelRef.current?.contains(event.target as Node) &&
        !triggerRef.current?.contains(event.target as Node)
      ) {
        setPanelOpen(false);
      }
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [panelOpen]);

  if (!published) return null;

  const getShareDetails = () => {
    const url = canonicalUrlForCurrentPage();
    const text = `${title}\n\n${summary}\n\n${copy.readOn}`;
    return { url, text };
  };

  const openPanel = () => {
    setCopyState("idle");
    setPanelOpen(true);
  };

  const share = async () => {
    if (panelOpen) {
      setPanelOpen(false);
      return;
    }
    const { url, text } = getShareDetails();
    if (typeof navigator.share === "function" && isCompactShareDevice()) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (error) {
        if ((error as DOMException).name === "AbortError") return;
      }
    }
    openPanel();
  };

  const copyLink = async () => {
    try {
      const copied = await copyCanonicalUrl(getShareDetails().url);
      setCopyState(copied ? "copied" : "failed");
    } catch {
      setCopyState("failed");
    }
  };

  const { url, text } = getShareDetails();
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);

  return (
    <div className="article-share">
      <button
        ref={triggerRef}
        className="button secondary"
        type="button"
        aria-label={copy.shareArticleAria}
        aria-expanded={panelOpen}
        aria-controls={panelId}
        onClick={() => void share()}
      >
        {copy.shareArticle}
      </button>
      {panelOpen && (
        <div
          ref={panelRef}
          id={panelId}
          className="article-share-panel"
          role="dialog"
          aria-label={copy.sharePanelTitle}
        >
          <p>{copy.sharePanelTitle}</p>
          <div className="article-share-options">
            <a
              href={`https://api.whatsapp.com/send?text=${encodedText}%0A${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              {copy.shareWhatsApp}
            </a>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              {copy.shareLinkedIn}
            </a>
            <a
              href={`https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodedUrl}`}
              target="_blank"
              rel="noreferrer"
            >
              {copy.shareX}
            </a>
            <button type="button" onClick={() => void copyLink()}>
              {copy.copyLink}
            </button>
          </div>
          <p className="article-share-status" aria-live="polite">
            {copyState === "copied"
              ? copy.linkCopied
              : copyState === "failed"
                ? copy.copyLinkFailed
                : ""}
          </p>
        </div>
      )}
    </div>
  );
}

const questionCopy = {
  en: {
    nav: "Questions",
    indexKicker: "HeartSpeaks questions",
    indexTitle: "Questions people are sitting with.",
    indexBody:
      "A quiet place to reflect in private, or share an experience for careful editorial review.",
    emptyTitle: "The first Questions are being prepared.",
    emptyBody:
      "When a Question is ready, it will make room for private reflection and selected reader voices.",
    questionKicker: "A question worth sitting with",
    privateTitle: "Keep your first response private",
    privateBody:
      "This draft stays on this device. HeartSpeaks does not receive it unless you deliberately choose to share.",
    placeholder: "Write what this brings up for you…",
    savePrivate: "Save privately on this device",
    savedPrivate: "Saved on this device",
    deletePrivate: "Delete private draft",
    share: "Share with HeartSpeaks",
    shareTitle: "Share your reflection",
    shareBody:
      "Share what your own experience taught you. Please do not prescribe what someone else should do.",
    identityLabel: "How should we show your reflection?",
    consent:
      "I understand that HeartSpeaks may edit this reflection for clarity and publish it only after human editorial review. I can request removal later.",
    reuseConsent:
      "HeartSpeaks may also consider an anonymised excerpt in a future editorial synthesis. This is optional and is not needed for publication here.",
    preview: "Preview your reflection",
    submit: "Send for careful review",
    sending: "Sending…",
    successTitle: "Your reflection is with us.",
    successBody: "A human editor will review it before anything is published.",
    error: "We could not save this right now. Please try again.",
    source: "Where this question began",
    readSource: "Read the Reflection",
    readConversation: "Read the Conversation",
    voices: "Selected reader voices",
    voicesBody:
      "Independent reflections shared with permission and published after editorial review.",
    voicesLoading: "Loading reader voices…",
    voicesUnavailable:
      "Reader voices are temporarily unavailable. You can still reflect privately.",
    voicesEmpty:
      "No reader reflections have been published here yet. You can be among the first to share one.",
    countOne: "published reader voice",
    countMany: "published reader voices",
    reflect: "Reflect",
    readVoices: "Read reader voices",
    report: "Report this reflection",
    reportPrompt: "What needs our attention?",
    reportPrivacy: "Privacy",
    reportHarm: "Harm",
    reportOther: "Other",
    reportSent: "Thank you. An editor will review this report.",
    reportError: "We could not send that report. Please try again.",
    questionNotFoundTitle: "This question is not available.",
    questionNotFoundBody:
      "It may still be an editorial draft, or the address may be incorrect.",
    questionNotFoundReturn: "Browse questions",
  },
  ta: {
    nav: "கேள்விகள்",
    indexKicker: "HeartSpeaks கேள்விகள்",
    indexTitle: "மக்கள் நிதானமாகச் சிந்தித்துக் கொண்டிருக்கும் கேள்விகள்.",
    indexBody:
      "தனிப்பட்ட முறையில் சிந்திக்கவும் அல்லது கவனமான ஆசிரியர் பரிசீலனைக்காக உங்கள் அனுபவத்தைப் பகிரவும் ஒரு அமைதியான இடம்.",
    emptyTitle: "முதல் கேள்விகள் தயாராகிக் கொண்டிருக்கின்றன.",
    emptyBody:
      "ஒரு கேள்வி தயாரானதும், அது தனிப்பட்ட சிந்தனைக்கும் தேர்ந்தெடுக்கப்பட்ட வாசகர் குரல்களுக்கும் இடம் தரும்.",
    questionKicker: "நிதானமாகச் சிந்திக்க வேண்டிய ஒரு கேள்வி",
    privateTitle: "உங்கள் முதல் பதிலை தனிப்பட்டதாக வைத்துக்கொள்ளுங்கள்",
    privateBody:
      "இந்த வரைவு இந்தச் சாதனத்தில் மட்டுமே இருக்கும். நீங்கள் விரும்பி பகிரும் வரை HeartSpeaks இதைப் பெறாது.",
    placeholder: "இந்தக் கேள்வி உங்களுக்குள் எழுப்புவதை எழுதுங்கள்…",
    savePrivate: "இந்தச் சாதனத்தில் தனிப்பட்ட முறையில் சேமிக்கவும்",
    savedPrivate: "இந்தச் சாதனத்தில் சேமிக்கப்பட்டது",
    deletePrivate: "தனிப்பட்ட வரைவை நீக்கவும்",
    share: "HeartSpeaks உடன் பகிரவும்",
    shareTitle: "உங்கள் சிந்தனையைப் பகிருங்கள்",
    shareBody:
      "உங்கள் அனுபவம் உங்களுக்குக் கற்றுத் தந்ததைப் பகிருங்கள். மற்றவர் என்ன செய்ய வேண்டும் என்று அறிவுரை கூற வேண்டாம்.",
    identityLabel: "உங்கள் சிந்தனையை எப்படிக் காட்ட வேண்டும்?",
    consent:
      "தெளிவுக்காக HeartSpeaks இந்தச் சிந்தனையைத் திருத்தி, மனித ஆசிரியர் பரிசீலனைக்குப் பிறகே வெளியிடலாம் என்பதைப் புரிந்துகொள்கிறேன். பின்னர் அதை நீக்கக் கோரலாம்.",
    reuseConsent:
      "எதிர்கால ஆசிரியர் தொகுப்பில் பெயரில்லாத ஒரு பகுதியைப் பயன்படுத்த HeartSpeaks பரிசீலிக்கலாம். இது விருப்பமானது; இங்கே வெளியிட இது தேவையில்லை.",
    preview: "உங்கள் சிந்தனையை முன்பார்வையிடுங்கள்",
    submit: "கவனமான பரிசீலனைக்கு அனுப்பவும்",
    sending: "அனுப்பப்படுகிறது…",
    successTitle: "உங்கள் சிந்தனை எங்களிடம் உள்ளது.",
    successBody:
      "எதையும் வெளியிடுவதற்கு முன் ஒரு மனித ஆசிரியர் அதை பரிசீலிப்பார்.",
    error: "இப்போது சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
    source: "இந்தக் கேள்வி தொடங்கிய இடம்",
    readSource: "சிந்தனையை வாசிக்கவும்",
    readConversation: "உரையாடலை வாசிக்கவும்",
    voices: "தேர்ந்தெடுக்கப்பட்ட வாசகர் குரல்கள்",
    voicesBody:
      "அனுமதியுடன் பகிரப்பட்டு, ஆசிரியர் பரிசீலனைக்குப் பிறகு வெளியிடப்பட்ட தனித்தனி சிந்தனைகள்.",
    voicesLoading: "வாசகர் குரல்கள் ஏற்றப்படுகின்றன…",
    voicesUnavailable:
      "வாசகர் குரல்கள் தற்காலிகமாகக் கிடைக்கவில்லை. நீங்கள் தனிப்பட்ட முறையில் சிந்திக்கலாம்.",
    voicesEmpty:
      "இங்கே இன்னும் எந்த வாசகர் சிந்தனையும் வெளியிடப்படவில்லை. முதலில் பகிர்பவர்களில் ஒருவராக நீங்கள் இருக்கலாம்.",
    countOne: "வெளியிடப்பட்ட வாசகர் குரல்",
    countMany: "வெளியிடப்பட்ட வாசகர் குரல்கள்",
    reflect: "சிந்தியுங்கள்",
    readVoices: "வாசகர் குரல்களை வாசிக்கவும்",
    report: "இந்தச் சிந்தனையைப் புகாரளிக்கவும்",
    reportPrompt: "எது எங்கள் கவனத்திற்குத் தேவை?",
    reportPrivacy: "தனியுரிமை",
    reportHarm: "பாதிப்பு",
    reportOther: "மற்றவை",
    reportSent: "நன்றி. ஒரு ஆசிரியர் இந்தப் புகாரைப் பார்ப்பார்.",
    reportError: "அந்தப் புகாரை அனுப்ப முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
    questionNotFoundTitle: "இந்தக் கேள்வி கிடைக்கவில்லை.",
    questionNotFoundBody:
      "இது இன்னும் ஆசிரியர் வரைவாக இருக்கலாம் அல்லது முகவரி தவறாக இருக்கலாம்.",
    questionNotFoundReturn: "கேள்விகளைப் பாருங்கள்",
  },
} as const;

const pageCopy = {
  en: {
    conversations: {
      kicker: "HeartSpeaks conversations",
      title: "Questions that open a life.",
      intro:
        "A conversation begins with one human question, then makes room for reflection, different perspectives, and your own experience.",
      current: "Featured conversation",
      read: "Read the conversation",
      next: "Questions in preparation",
      nextIntro:
        "A small editorial queue—not an endless feed. Published conversations appear here as they are ready.",
      anatomy: "What a conversation holds",
      anatomyItems: [
        {
          title: "Reflection",
          body: "An original HeartSpeaks exploration of the question.",
        },
        {
          title: "Perspectives",
          body: "Different ways the same question can appear across lives.",
        },
        {
          title: "Your experience",
          body: "A private or consented response, without public debate.",
        },
      ],
      storiesTitle: "Prefer to begin with a person, not a question?",
      storiesBody:
        "Stories follow one lived experience from circumstance to turning point.",
      storiesLink: "Explore stories",
    },
    stories: {
      kicker: "HeartSpeaks stories",
      title: "Stories from ordinary lives.",
      intro:
        "One person’s circumstances, choices, turning point, and what they understand now—published with a clear source and careful consent.",
      collection: "Published stories",
      emptyKicker: "An honest beginning",
      typesTitle: "Every story tells you what it is",
      types: [
        {
          title: "Reader story",
          body: "Submitted to HeartSpeaks and published only with explicit consent.",
        },
        {
          title: "Interview",
          body: "Recorded or gathered directly by HeartSpeaks with the participant’s permission.",
        },
        {
          title: "Editorial story",
          body: "Independently written from a disclosed source; never a disguised transcript or invented reader.",
        },
      ],
      standards: "Read our editorial standards",
    },
    reflections: {
      kicker: "HeartSpeaks reflections",
      title: "Ideas brought back to life.",
      intro:
        "A book, poem, podcast, film, or ordinary observation can make us notice something about being human. The source begins the thought; our lives are where it lands.",
      collection: "Published reflections",
      emptyTitle: "The first reflection is being prepared.",
      emptyBody:
        "Reflections appear here after source verification, bilingual review, and publication checks are complete.",
      source: "Where the idea began",
      read: "Read the reflection",
      approved: "Editorially approved · awaiting publication",
      metadata: "Explore this reflection",
      series: "Series",
      topics: "Topics",
      heartPrompt: "If this stayed with you, leave a heart.",
      heart: "This stayed with me",
      hearted: "This stayed with you",
      heartSaving: "Saving your heart…",
      heartRemove: "Remove heart",
      heartRetry: "Try again",
      heartError: "We couldn’t save that change. Please try again.",
      heartOne: "heart",
      heartMany: "hearts",
      methodTitle: "Source first. Human question always.",
      methodBody:
        "We do not publish summaries or borrowed wisdom. Each Reflection credits its source, develops an original HeartSpeaks interpretation, makes room for a counter-reading, and returns the idea to your own life.",
      beginReading: "Begin reading",
      readingGuide: "Inside this reflection",
      readingProgress: "Reading progress",
      reflectionLabel: "Reflections",
      tocLabel: "This reflection",
      contents: "Contents",
      closeContents: "Close contents",
      expandContents: "Expand contents",
      collapseContents: "Collapse contents",
      backToTop: "Back to top",
    },
    about: {
      kicker: "About HeartSpeaks",
      title: "Why this space exists.",
      lead: "Many of the most useful things we learn about life come from hearing what another person actually lived through.",
      sections: [
        {
          title: "One question. Many lives.",
          body: "HeartSpeaks begins with questions that are easy to ask but difficult to answer. We publish original reflections and carefully presented experiences around family, work, relationships, money, ageing, happiness, regret, and ordinary life.",
        },
        {
          title: "Experience over opinion",
          body: "There are no winners here. The purpose is not to decide which side is right, but to understand what the same question can look like through different lives.",
        },
        {
          title: "Consent before publication",
          body: "A reader may reflect privately, send an experience for private review, or permit publication anonymously or with a first name. Nothing submitted is published automatically.",
        },
      ],
    },
    privacyPage: {
      kicker: "Privacy",
      title: "Your words deserve careful handling.",
      updated: "Pilot privacy notice · 14 August 2026",
      sections: [
        {
          title: "What we collect",
          body: "When you share an experience, HeartSpeaks stores your response, selected language, publication preference, consent record, and any optional first name or age range you provide. A private reflection saved on the page remains only in your browser and is not sent to HeartSpeaks. If you leave or remove a heart on a Reflection, we retain only aggregate counts. A random request identifier is marked for automatic deletion after 24 hours solely to prevent a technical retry from changing the count twice; it is not linked to a name, email, or account.",
        },
        {
          title: "How it is used",
          body: "Responses are reviewed to understand the conversation and, only when you grant publication permission, may be edited for clarity or privacy and considered for publication. Submissions never appear publicly without editorial review.",
        },
        {
          title: "Storage and access",
          body: "The website and submission service run on Amazon Web Services. Editorial access is restricted to authenticated HeartSpeaks editors. We do not place story text in analytics events or routine application logs.",
        },
        {
          title: "Removal and contact",
          body: "You may request removal of a published story or ask a privacy question by emailing hello@heartspeaks.in. Please include enough context for us to locate the relevant submission without sending additional sensitive information.",
        },
      ],
    },
    guidelinesPage: {
      kicker: "Editorial guidelines",
      title: "A space for experience, reflection, and care.",
      lead: "HeartSpeaks publishes lived experiences—not arguments, rankings, or instant comments.",
      sections: [
        {
          title: "What we welcome",
          body: "First-person experiences, thoughtful reflection, nuance, respectful disagreement, and answers written in Tamil or English. You do not need to be a polished writer.",
        },
        {
          title: "What we do not publish",
          body: "Harassment, hate, threats, promotional spam, copied material, contact details, financial information, doxxing, or unsupported allegations presented as fact.",
        },
        {
          title: "How editing works",
          body: "HeartSpeaks may edit or decline a submission for clarity, privacy, relevance, safety, or editorial quality. The original response remains unchanged in our editorial record, and substantial changes require appropriate confirmation when contact is possible.",
        },
        {
          title: "External inspiration",
          body: "A public conversation, programme, book, or interview may inspire a question. HeartSpeaks does not reproduce complete transcripts or substantial dialogue; its editorial reflection and reader question are independently developed.",
        },
      ],
    },
  },
  ta: {
    conversations: {
      kicker: "HeartSpeaks உரையாடல்கள்",
      title: "ஒரு வாழ்க்கையைத் திறக்கும் கேள்விகள்.",
      intro:
        "ஒரு மனிதக் கேள்வியுடன் உரையாடல் தொடங்குகிறது; பின்னர் சிந்தனைக்கும், வெவ்வேறு கண்ணோட்டங்களுக்கும், உங்கள் அனுபவத்திற்கும் இடமளிக்கிறது.",
      current: "சிறப்பு உரையாடல்",
      read: "உரையாடலை வாசிக்கவும்",
      next: "தயாராகும் கேள்விகள்",
      nextIntro:
        "இது முடிவில்லாத செய்தியோடை அல்ல; கவனமாகத் தயாராகும் சிறிய ஆசிரியர் வரிசை.",
      anatomy: "ஒரு உரையாடலில் இருப்பவை",
      anatomyItems: [
        {
          title: "சிந்தனை",
          body: "கேள்வியை ஆராயும் HeartSpeaks அசல் ஆசிரியர் பதிவு.",
        },
        {
          title: "கண்ணோட்டங்கள்",
          body: "ஒரே கேள்வி வெவ்வேறு வாழ்க்கைகளில் தோன்றும் விதங்கள்.",
        },
        {
          title: "உங்கள் அனுபவம்",
          body: "பொது விவாதமின்றி தனிப்பட்ட அல்லது அனுமதியளிக்கப்பட்ட பதில்.",
        },
      ],
      storiesTitle:
        "கேள்வியை விட ஒரு மனிதரிடமிருந்து தொடங்க விரும்புகிறீர்களா?",
      storiesBody:
        "கதைகள் ஒருவரின் வாழ்க்கை அனுபவத்தைச் சூழலிலிருந்து திருப்புமுனை வரை பின்தொடர்கின்றன.",
      storiesLink: "கதைகளை ஆராயுங்கள்",
    },
    stories: {
      kicker: "HeartSpeaks கதைகள்",
      title: "சாதாரண வாழ்க்கைகளிலிருந்து கதைகள்.",
      intro:
        "ஒருவரின் சூழல், தேர்வுகள், திருப்புமுனை, இன்று அவர் புரிந்துகொள்வது—தெளிவான ஆதாரமும் கவனமான அனுமதியும் உடன் வெளியிடப்படும்.",
      collection: "வெளியிடப்பட்ட கதைகள்",
      emptyKicker: "ஒரு நேர்மையான தொடக்கம்",
      typesTitle: "ஒவ்வொரு கதையும் அது என்ன என்பதைச் சொல்கிறது",
      types: [
        {
          title: "வாசகர் கதை",
          body: "HeartSpeaks-க்கு அனுப்பப்பட்டு வெளிப்படையான அனுமதியுடன் மட்டுமே வெளியிடப்படும்.",
        },
        {
          title: "நேர்காணல்",
          body: "பங்கேற்பாளரின் அனுமதியுடன் HeartSpeaks நேரடியாகச் சேகரித்த அனுபவம்.",
        },
        {
          title: "ஆசிரியர் கதை",
          body: "வெளிப்படையாகக் குறிப்பிடப்பட்ட ஆதாரத்திலிருந்து சுயமாக எழுதப்பட்டது; மறைக்கப்பட்ட உரைநகலோ உருவாக்கப்பட்ட வாசகரோ அல்ல.",
        },
      ],
      standards: "எங்கள் ஆசிரியர் தரநிலைகளை வாசிக்கவும்",
    },
    reflections: {
      kicker: "HeartSpeaks சிந்தனைகள்",
      title: "வாழ்க்கைக்குத் திரும்பும் எண்ணங்கள்.",
      intro:
        "ஒரு நூல், கவிதை, பாட்காஸ்ட், திரைப்படம் அல்லது அன்றாடக் கவனிப்பு—மனித வாழ்வைப் பற்றி ஏதோ ஒன்றை நாம் புதிதாகப் பார்க்கச் செய்யலாம். மூலம் சிந்தனையைத் தொடங்குகிறது; அது நமது வாழ்க்கையில் வந்து சேர்கிறது.",
      collection: "வெளியிடப்பட்ட சிந்தனைகள்",
      emptyTitle: "முதல் சிந்தனை தயாராகிறது.",
      emptyBody:
        "மூலச் சரிபார்ப்பு, இருமொழி மதிப்பாய்வு மற்றும் வெளியீட்டுச் சோதனைகள் முடிந்த பிறகு சிந்தனைகள் இங்கே தோன்றும்.",
      source: "இந்த எண்ணம் தொடங்கிய இடம்",
      read: "சிந்தனையை வாசிக்கவும்",
      approved: "ஆசிரியர் ஒப்புதல் பெற்றது · வெளியீட்டிற்காகக் காத்திருக்கிறது",
      metadata: "இந்தச் சிந்தனையைத் தொடர்ந்து வாசிக்க",
      series: "தொடர்",
      topics: "தலைப்புகள்",
      heartPrompt: "இது மனதில் தங்கியிருந்தால், ஒரு இதயக் குறியை விடலாம்.",
      heart: "இது மனதில் நின்றது",
      hearted: "இது மனதில் நின்றது",
      heartSaving: "உங்கள் இதயக் குறி சேமிக்கப்படுகிறது…",
      heartRemove: "இதயக் குறியை நீக்கவும்",
      heartRetry: "மீண்டும் முயற்சிக்கவும்",
      heartError:
        "இந்த மாற்றத்தை இப்போது சேமிக்க முடியவில்லை. மீண்டும் முயற்சிக்கவும்.",
      heartOne: "இதயக் குறி",
      heartMany: "இதயக் குறிகள்",
      methodTitle: "மூலம் முதலில். மனிதக் கேள்வி எப்போதும்.",
      methodBody:
        "நாங்கள் சுருக்கங்களையோ கடன் வாங்கிய அறிவுரைகளையோ வெளியிடுவதில்லை. ஒவ்வொரு சிந்தனையும் அதன் மூலத்தைத் தெளிவாகக் குறிப்பிடுகிறது; HeartSpeaks-ன் அசல் பார்வையை வளர்க்கிறது; மாற்றுப் பார்வைக்கு இடமளிக்கிறது; இறுதியில் அந்த எண்ணத்தை உங்கள் வாழ்க்கைக்குத் திருப்புகிறது.",
      beginReading: "வாசிக்கத் தொடங்குங்கள்",
      readingGuide: "இந்தச் சிந்தனையின் உள்ளே",
      readingProgress: "வாசிப்பு முன்னேற்றம்",
      reflectionLabel: "சிந்தனைகள்",
      tocLabel: "இந்தச் சிந்தனை",
      contents: "உள்ளடக்கம்",
      closeContents: "உள்ளடக்கத்தை மூடவும்",
      expandContents: "உள்ளடக்கத்தை விரிக்கவும்",
      collapseContents: "உள்ளடக்கத்தைச் சுருக்கவும்",
      backToTop: "மேலே செல்லவும்",
    },
    about: {
      kicker: "HeartSpeaks பற்றி",
      title: "இந்த இடம் ஏன் உருவானது.",
      lead: "வாழ்க்கையில் நாம் கற்றுக்கொள்ளும் பயனுள்ள பல விஷயங்கள், இன்னொருவர் உண்மையில் வாழ்ந்த அனுபவத்தைக் கேட்பதிலிருந்து வருகின்றன.",
      sections: [
        {
          title: "ஒரு கேள்வி. பல வாழ்க்கைகள்.",
          body: "கேட்பதற்கு எளிதாகவும் பதிலளிப்பதற்கு கடினமாகவும் இருக்கும் கேள்விகளுடன் HeartSpeaks தொடங்குகிறது. குடும்பம், வேலை, உறவுகள், பணம், வயதாகுதல், மகிழ்ச்சி, வருத்தம் மற்றும் சாதாரண வாழ்க்கை குறித்த அசல் சிந்தனைகளையும் கவனமாகத் தொகுக்கப்பட்ட அனுபவங்களையும் வெளியிடுகிறோம்.",
        },
        {
          title: "கருத்தை விட அனுபவம்",
          body: "இங்கே வெற்றியாளர்கள் இல்லை. எந்தப் பக்கம் சரி என்று தீர்மானிப்பது நோக்கமல்ல; ஒரே கேள்வி வெவ்வேறு வாழ்க்கைகளில் எப்படி தோன்றுகிறது என்பதைப் புரிந்துகொள்வதே நோக்கம்.",
        },
        {
          title: "வெளியீட்டிற்கு முன் அனுமதி",
          body: "ஒரு வாசகர் தனிப்பட்ட முறையில் சிந்திக்கலாம், தனிப்பட்ட பரிசீலனைக்காக அனுபவத்தை அனுப்பலாம், அல்லது பெயரில்லாமலோ முதல் பெயருடனோ வெளியிட அனுமதிக்கலாம். அனுப்பப்படும் எதுவும் தானாக வெளியிடப்படாது.",
        },
      ],
    },
    privacyPage: {
      kicker: "தனியுரிமை",
      title: "உங்கள் வார்த்தைகள் கவனமாகக் கையாளப்பட வேண்டும்.",
      updated: "முன்னோட்ட தனியுரிமை அறிவிப்பு · 14 ஆகஸ்ட் 2026",
      sections: [
        {
          title: "நாங்கள் சேகரிப்பவை",
          body: "நீங்கள் அனுபவத்தைப் பகிரும்போது, உங்கள் பதில், தேர்ந்தெடுத்த மொழி, வெளியீட்டு விருப்பம், அனுமதி பதிவு, நீங்கள் விருப்பத்துடன் தரும் முதல் பெயர் அல்லது வயது வரம்பு ஆகியவை சேமிக்கப்படும். பக்கத்தில் தனிப்பட்ட முறையில் சேமிக்கும் சிந்தனை உங்கள் உலாவியிலேயே இருக்கும்; HeartSpeaks-க்கு அனுப்பப்படாது. ஒரு சிந்தனையில் இதயக் குறியை இடும்போதோ நீக்கும்போதோ, மொத்த எண்ணிக்கை மட்டுமே நிரந்தரமாகச் சேமிக்கப்படும். தொழில்நுட்ப ரீதியான மீள்முயற்சி எண்ணிக்கையை இருமுறை மாற்றாமல் இருக்க, சீரற்ற கோரிக்கை அடையாளம் 24 மணி நேரத்திற்குப் பிறகு தானாக நீக்கப்படுமாறு குறிக்கப்படும்; அது பெயர், மின்னஞ்சல் அல்லது கணக்குடன் இணைக்கப்படாது.",
        },
        {
          title: "அது பயன்படுத்தப்படும் விதம்",
          body: "உரையாடலைப் புரிந்துகொள்ள பதில்கள் பரிசீலிக்கப்படும். வெளியீட்டு அனுமதி அளித்தால் மட்டுமே, தெளிவு அல்லது தனியுரிமைக்காகத் திருத்தப்பட்டு வெளியீட்டிற்கு பரிசீலிக்கப்படலாம். ஆசிரியர் பரிசீலனை இல்லாமல் எதுவும் பொதுவாகத் தோன்றாது.",
        },
        {
          title: "சேமிப்பு மற்றும் அணுகல்",
          body: "இணையதளமும் பதில் சேவையும் Amazon Web Services-ல் இயங்குகின்றன. அங்கீகரிக்கப்பட்ட HeartSpeaks ஆசிரியர்களுக்கு மட்டுமே ஆசிரியர் அணுகல் உள்ளது. கதை உரையை பகுப்பாய்வு நிகழ்வுகளிலோ வழக்கமான பயன்பாட்டு பதிவுகளிலோ சேர்ப்பதில்லை.",
        },
        {
          title: "நீக்கம் மற்றும் தொடர்பு",
          body: "வெளியிடப்பட்ட கதையை நீக்கக் கோரவோ தனியுரிமை கேள்வி கேட்கவோ hello@heartspeaks.in என்ற முகவரிக்கு எழுதலாம். கூடுதல் தனிப்பட்ட தகவலை அனுப்பாமல், தொடர்புடைய பதிலை கண்டறியத் தேவையான விவரத்தை மட்டும் சேர்க்கவும்.",
        },
      ],
    },
    guidelinesPage: {
      kicker: "ஆசிரியர் வழிகாட்டுதல்கள்",
      title: "அனுபவம், சிந்தனை, அக்கறைக்கான ஓர் இடம்.",
      lead: "HeartSpeaks வாழ்க்கை அனுபவங்களை வெளியிடுகிறது—வாதங்கள், தரவரிசைகள் அல்லது உடனடி கருத்துகளை அல்ல.",
      sections: [
        {
          title: "நாங்கள் வரவேற்பவை",
          body: "முதல் நபர் அனுபவங்கள், ஆழ்ந்த சிந்தனை, நுணுக்கம், மரியாதையான வேறுபாடு, தமிழ் அல்லது ஆங்கிலத்தில் எழுதப்பட்ட பதில்கள். நீங்கள் தேர்ந்த எழுத்தாளராக இருக்க வேண்டியதில்லை.",
        },
        {
          title: "நாங்கள் வெளியிடாதவை",
          body: "துன்புறுத்தல், வெறுப்பு, மிரட்டல், விளம்பரத் தகவல், நகலெடுத்த உள்ளடக்கம், தொடர்பு விவரங்கள், நிதித் தகவல், தனிப்பட்ட தகவல் வெளிப்படுத்தல் அல்லது ஆதாரமற்ற குற்றச்சாட்டுகள்.",
        },
        {
          title: "திருத்தம் செய்யும் விதம்",
          body: "தெளிவு, தனியுரிமை, பொருத்தம், பாதுகாப்பு அல்லது ஆசிரியர் தரத்திற்காக ஒரு பதிலை HeartSpeaks திருத்தவோ நிராகரிக்கவோலாம். அசல் பதில் ஆசிரியர் பதிவில் மாற்றமின்றி இருக்கும்.",
        },
        {
          title: "வெளிப்புறத் தூண்டுதல்",
          body: "ஒரு பொது உரையாடல், நிகழ்ச்சி, புத்தகம் அல்லது நேர்காணல் ஒரு கேள்விக்கு தூண்டுதலாக இருக்கலாம். முழு உரைநகலையோ நீண்ட உரையாடலையோ HeartSpeaks மீள வெளியிடாது; ஆசிரியர் சிந்தனையும் வாசகர் கேள்வியும் தனித்துவமாக உருவாக்கப்படும்.",
        },
      ],
    },
  },
} as const;

type LibraryContentType = "conversation" | "story" | "reflection" | "question";
type LibraryFilter = "all" | LibraryContentType;

type LibraryLocale = {
  title: string;
  summary: string;
  topic: string;
  body: string;
};

type LibraryItem = {
  id: string;
  href: string;
  type: LibraryContentType;
  status: "published" | "approved";
  publishedAt: string;
  readingMinutes?: number;
  image?: string;
  series?: string;
  tags: readonly string[];
  locales: Record<Language, LibraryLocale>;
};

function htmlToPlainText(value: string) {
  return value
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeSearch(value: string) {
  return value
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
}

const libraryItems: readonly LibraryItem[] = [
  ...conversationViews
    .filter((item) => item.status === "published")
    .map((item) => ({
      id: item.id,
      href: `/conversations/${item.slug}/`,
      type: "conversation" as const,
      status: "published" as const,
      publishedAt: item.publishedAt,
      readingMinutes: item.readingMinutes,
      image: item.socialImage,
      tags: [item.topic],
      locales: {
        en: {
          title: item.en.title,
          summary: item.en.summary,
          topic: item.topic,
          body: htmlToPlainText(item.en.bodyHtml),
        },
        ta: {
          title: item.ta.title,
          summary: item.ta.summary,
          topic: item.topic,
          body: htmlToPlainText(item.ta.bodyHtml),
        },
      },
    })),
  ...storyViews
    .filter((item) => item.status === "published")
    .map((item) => ({
      id: item.id,
      href: `/stories/${item.slug}/`,
      type: "story" as const,
      status: "published" as const,
      publishedAt: item.publishedAt,
      readingMinutes: item.readingMinutes,
      image: item.socialImage,
      tags: [item.categoryKey, item.storyType],
      locales: {
        en: {
          title: item.en.title,
          summary: item.en.summary,
          topic: item.en.category,
          body: htmlToPlainText(item.en.bodyHtml),
        },
        ta: {
          title: item.ta.title,
          summary: item.ta.summary,
          topic: item.ta.category,
          body: htmlToPlainText(item.ta.bodyHtml),
        },
      },
    })),
  ...reflectionViews
    .filter((item) => item.status === "published" || item.status === "approved")
    .map((item) => ({
      id: item.id,
      href: `/reflections/${item.slug}/`,
      type: "reflection" as const,
      status: item.status as "published" | "approved",
      publishedAt: item.publishedAt || "",
      readingMinutes: item.readingMinutes,
      image: item.socialImage,
      series: item.series,
      tags: item.tags,
      locales: {
        en: {
          title: item.en.title,
          summary: item.en.summary,
          topic: item.en.topic,
          body: htmlToPlainText(item.en.bodyHtml),
        },
        ta: {
          title: item.ta.title,
          summary: item.ta.summary,
          topic: item.ta.topic,
          body: htmlToPlainText(item.ta.bodyHtml),
        },
      },
    })),
  ...questionViews
    .filter((item) => item.status === "published")
    .map((item) => ({
      id: item.id,
      href: "/questions/" + item.slug + "/",
      type: "question" as const,
      status: "published" as const,
      publishedAt: item.publishedAt || "",
      tags: [item.topic],
      locales: {
        en: {
          title: item.en.question,
          summary: item.en.summary,
          topic: item.topic,
          body: item.en.question,
        },
        ta: {
          title: item.ta.question,
          summary: item.ta.summary,
          topic: item.topic,
          body: item.ta.question,
        },
      },
    })),
].sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));

function libraryTypeLabel(language: Language, type: LibraryContentType) {
  const copy = ui[language];
  if (type === "conversation") return copy.conversationType;
  if (type === "story") return copy.storyType;
  if (type === "question") return questionCopy[language].nav;
  return copy.reflectionType;
}

function formatPublishedDate(language: Language, value: string) {
  if (!value) return "";
  return new Intl.DateTimeFormat(language === "ta" ? "ta-IN" : "en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function reflectionExploreHref(query: string, filter?: "reflection") {
  const params = new URLSearchParams({ q: query });
  if (filter) params.set("type", filter);
  return `/explore/?${params.toString()}`;
}

const reflectionMetadataTamil: Readonly<Record<string, string>> = {
  "Tamil thought, carried forward": "தொடரும் தமிழ்ச் சிந்தனைகள்",
  anger: "கோபம்",
  boundaries: "எல்லைகள்",
  change: "மாற்றம்",
  conflict: "மோதல்",
  empathy: "அக்கறை",
  friendship: "நட்பு",
  generosity: "கொடுக்கும் மனம்",
  growth: "வளர்ச்சி",
  harm: "காயம்",
  humility: "பணிவு",
  identity: "அடையாளம்",
  inheritance: "மரபாக வந்தவை",
  learning: "கற்றல்",
  money: "பணம்",
  relationships: "உறவுகள்",
  responsibility: "பொறுப்பு",
  scarcity: "பற்றாக்குறை",
  "self-respect": "சுயமரியாதை",
  support: "துணை",
  tradition: "மரபு",
  uncertainty: "நிச்சயமின்மை",
};

function displayReflectionMetadata(language: Language, value: string) {
  return language === "ta" ? reflectionMetadataTamil[value] || value : value;
}

function libraryScore(item: LibraryItem, query: string) {
  const normalizedQuery = normalizeSearch(query);
  if (!normalizedQuery) return 0;
  const tokens = normalizedQuery.split(" ").filter(Boolean);
  const localeValues = (Object.keys(item.locales) as Language[]).map(
    (language) => item.locales[language],
  );
  const titles = normalizeSearch(
    localeValues.map((item) => item.title).join(" "),
  );
  const topics = normalizeSearch(
    [
      ...localeValues.map((item) => item.topic),
      item.series || "",
      ...item.tags,
    ].join(" "),
  );
  const summaries = normalizeSearch(
    localeValues.map((item) => item.summary).join(" "),
  );
  const bodies = normalizeSearch(
    localeValues.map((item) => item.body).join(" "),
  );
  const allText = `${titles} ${topics} ${summaries} ${bodies}`;
  if (!tokens.every((token) => allText.includes(token))) return -1;
  let score = titles.includes(normalizedQuery) ? 30 : 0;
  if (topics.includes(normalizedQuery)) score += 18;
  if (summaries.includes(normalizedQuery)) score += 10;
  if (bodies.includes(normalizedQuery)) score += 4;
  for (const token of tokens) {
    if (titles.includes(token)) score += 8;
    if (topics.includes(token)) score += 5;
    if (summaries.includes(token)) score += 3;
    if (bodies.includes(token)) score += 1;
  }
  return score;
}

function searchLibrary(query: string, filter: LibraryFilter) {
  return libraryItems
    .filter((item) => filter === "all" || item.type === filter)
    .map((item) => ({ item, score: libraryScore(item, query) }))
    .filter(({ score }) => !normalizeSearch(query) || score >= 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.item.publishedAt.localeCompare(a.item.publishedAt),
    )
    .map(({ item }) => item);
}

function LibrarySearchForm({
  language,
  value = "",
  onChange,
  onSubmit,
}: {
  language: Language;
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
}) {
  const copy = ui[language];
  return (
    <form
      className="library-search"
      action="/explore/"
      method="get"
      role="search"
      onSubmit={onSubmit}
    >
      <label htmlFor="library-search">{copy.searchAria}</label>
      <div>
        {onChange ? (
          <input
            id="library-search"
            name="q"
            type="search"
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder={copy.searchPlaceholder}
            autoComplete="off"
          />
        ) : (
          <input
            id="library-search"
            name="q"
            type="search"
            defaultValue={value}
            placeholder={copy.searchPlaceholder}
            autoComplete="off"
          />
        )}
        <button className="button primary" type="submit">
          {copy.search}
        </button>
      </div>
    </form>
  );
}

function LibraryCard({
  item,
  language,
  featured = false,
  priority = false,
}: {
  item: LibraryItem;
  language: Language;
  featured?: boolean;
  priority?: boolean;
}) {
  const copy = ui[language];
  const localized = item.locales[language];
  return (
    <article className={`library-card${featured ? " featured" : ""}`}>
      {item.image && (
        <a
          className="library-card-media"
          href={item.href}
          tabIndex={-1}
          aria-hidden="true"
        >
          <img
            src={item.image}
            width={1200}
            height={630}
            alt=""
            decoding="async"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
          />
        </a>
      )}
      <div className="library-card-body">
        <div className="library-card-labels">
          <span>{libraryTypeLabel(language, item.type)}</span>
          <span>{localized.topic}</span>
        </div>
        <h3>
          <a href={item.href}>{localized.title}</a>
        </h3>
        <p>{localized.summary}</p>
        <footer>
          <span>
            {copy.publishedLabel}{" "}
            {formatPublishedDate(language, item.publishedAt)}
          </span>
          {item.readingMinutes && (
            <span>
              {item.readingMinutes} {copy.minRead}
            </span>
          )}
        </footer>
        {item.status === "approved" && (
          <small>{pageCopy[language].reflections.approved}</small>
        )}
      </div>
    </article>
  );
}

function ConversationMedia({
  language,
  activeConversation,
  priority = false,
  className,
  sizes,
}: {
  language: Language;
  activeConversation: ConversationView;
  priority?: boolean;
  className: string;
  sizes: string;
}) {
  const image = activeConversation.heroImage;
  const media = activeConversation.heroMedia;
  const locale = activeConversation[language];
  if (!image || !media || activeConversation.heroLayout === "text") return null;
  const copy = ui[language];
  return (
    <figure className={className}>
      <EditorialImage
        image={image}
        alt={locale.imageAlt || ""}
        sizes={sizes}
        priority={priority}
      />
      {activeConversation.heroLayout === "image-quote" && locale.heroQuote && (
        <blockquote>“{locale.heroQuote}”</blockquote>
      )}
      <figcaption>
        {copy.editorialImage} · {media.credit}
        {!media.depictsContributors && <> · {copy.picturedNotContributors}</>}
      </figcaption>
    </figure>
  );
}

function storyTypeLabel(language: Language, story: StoryView) {
  const copy = ui[language];
  if (story.storyType === "reader") return copy.storyTypeReader;
  if (story.storyType === "interview") return copy.storyTypeInterview;
  return copy.storyTypeEditorial;
}

function StoryMedia({
  language,
  story,
  priority = false,
  className,
  sizes,
}: {
  language: Language;
  story: StoryView;
  priority?: boolean;
  className: string;
  sizes: string;
}) {
  if (!story.heroImage || !story.heroMedia || story.heroLayout === "text")
    return null;
  return (
    <figure className={className}>
      <EditorialImage
        image={story.heroImage}
        alt={story[language].imageAlt || ""}
        sizes={sizes}
        priority={priority}
      />
      <figcaption>
        {ui[language].editorialImage} · {story.heroMedia.credit}
        {!story.heroMedia.depictsContributors && (
          <> · {ui[language].picturedNotContributors}</>
        )}
      </figcaption>
    </figure>
  );
}

function Mark({ language }: { language: Language }) {
  return (
    <a className="mark" href="/" aria-label={ui[language].homeAria}>
      <img
        src="/brand/heartspeaks-wordmark.svg"
        width="380"
        height="96"
        alt=""
      />
    </a>
  );
}

function LanguageToggle({
  language,
  onChange,
}: {
  language: Language;
  onChange: (language: Language) => void;
}) {
  return (
    <div className="language" aria-label={ui[language].languageAria}>
      <button
        type="button"
        className={language === "en" ? "active" : ""}
        aria-pressed={language === "en"}
        onClick={() => onChange("en")}
      >
        EN
      </button>
      <span aria-hidden="true">|</span>
      <button
        type="button"
        className={language === "ta" ? "active" : ""}
        aria-pressed={language === "ta"}
        onClick={() => onChange("ta")}
      >
        தமிழ்
      </button>
    </div>
  );
}

function Header({
  language,
  onLanguage,
  path,
}: {
  language: Language;
  onLanguage: (language: Language) => void;
  path: string;
}) {
  const copy = ui[language];
  const [menuOpen, setMenuOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  const normalizedPath = path === "/" ? "/" : path.replace(/\/$/, "");
  const activeSection = normalizedPath.startsWith("/explore")
    ? "explore"
    : normalizedPath.startsWith("/stories")
      ? "stories"
      : normalizedPath.startsWith("/questions")
        ? "questions"
        : normalizedPath.startsWith("/reflections")
          ? "reflections"
          : normalizedPath.startsWith("/conversations")
            ? "conversations"
            : normalizedPath === "/about"
              ? "about"
              : undefined;
  useEffect(() => {
    if (!menuOpen) return;
    navigationRef.current?.querySelector<HTMLAnchorElement>("a")?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuToggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [menuOpen]);
  const closeMenu = () => setMenuOpen(false);
  return (
    <header className={`retention-header ${menuOpen ? "menu-open" : ""}`}>
      <Mark language={language} />
      <nav
        ref={navigationRef}
        id="primary-navigation"
        className={menuOpen ? "open" : ""}
        aria-label={copy.primaryNav}
      >
        <a
          className={activeSection === "explore" ? "active" : ""}
          aria-current={activeSection === "explore" ? "page" : undefined}
          href="/explore/"
          onClick={closeMenu}
        >
          {copy.exploreNav}
        </a>
        <a
          className={activeSection === "conversations" ? "active" : ""}
          aria-current={activeSection === "conversations" ? "page" : undefined}
          href="/conversations/"
          onClick={closeMenu}
        >
          {copy.nav[0]}
        </a>
        <a
          className={activeSection === "stories" ? "active" : ""}
          aria-current={activeSection === "stories" ? "page" : undefined}
          href="/stories/"
          onClick={closeMenu}
        >
          {copy.nav[1]}
        </a>
        {questionViews.length > 0 && (
          <a
            className={activeSection === "questions" ? "active" : ""}
            aria-current={activeSection === "questions" ? "page" : undefined}
            href="/questions/"
            onClick={closeMenu}
          >
            {questionCopy[language].nav}
          </a>
        )}
        <a
          className={activeSection === "reflections" ? "active" : ""}
          aria-current={activeSection === "reflections" ? "page" : undefined}
          href="/reflections/"
          onClick={closeMenu}
        >
          {copy.nav[2]}
        </a>
        <a
          className={activeSection === "about" ? "active" : ""}
          aria-current={activeSection === "about" ? "page" : undefined}
          href="/about/"
          onClick={closeMenu}
        >
          {copy.nav[3]}
        </a>
      </nav>
      <div className="header-actions">
        <LanguageToggle language={language} onChange={onLanguage} />
        <button
          ref={menuToggleRef}
          className="menu-toggle"
          type="button"
          aria-controls="primary-navigation"
          aria-expanded={menuOpen}
          aria-label={
            language === "en"
              ? menuOpen
                ? "Close menu"
                : "Open menu"
              : menuOpen
                ? "பட்டியலை மூடவும்"
                : "பட்டியலைத் திறக்கவும்"
          }
          onClick={() => setMenuOpen((current) => !current)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}

function Footer({ language }: { language: Language }) {
  const copy = ui[language];
  return (
    <footer className="retention-footer">
      <div className="footer-brand">
        <Mark language={language} />
        <p>{copy.footer}</p>
      </div>
      <div>
        <h3>{copy.explore}</h3>
        <a href="/explore/">{copy.exploreNav}</a>
        <a href="/conversations/">{copy.nav[0]}</a>
        <a href="/stories/">{copy.nav[1]}</a>
        {questionViews.length > 0 && (
          <a href="/questions/">{questionCopy[language].nav}</a>
        )}
        <a href="/reflections/">{copy.nav[2]}</a>
        <a href="/about/">{copy.nav[3]}</a>
      </div>
      <div>
        <h3>{copy.trustHeading}</h3>
        <a href="/privacy/">{copy.privacy}</a>
        <a href="/editorial-guidelines/">{copy.guidelines}</a>
        <a href="mailto:hello@heartspeaks.in?subject=Story removal request">
          {copy.removal}
        </a>
        <a href="mailto:hello@heartspeaks.in">{copy.contact}</a>
      </div>
      <div className="editorial-promise">
        <span>{copy.editorialPromise}</span>
        <p>{copy.editorialPromiseBody}</p>
      </div>
      <small>© 2026 HeartSpeaks</small>
    </footer>
  );
}

function NewsletterForm({ language }: { language: Language }) {
  const copy = ui[language];
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<
    "idle" | "sending" | "sent" | "local" | "error"
  >("idle");
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!consent) return;
    setState("sending");
    try {
      const result = await publicApi.subscribe({
        email,
        language,
        consent,
        website,
      });
      setState(result.deliveryConfigured ? "sent" : "local");
      publicApi.track("weekly_subscription", "/");
    } catch {
      setState("error");
    }
  };
  if (state === "sent" || state === "local")
    return (
      <div className="subscribe-result" role="status">
        <i>01</i>
        <p>{state === "sent" ? copy.subscribed : copy.localSubscribe}</p>
      </div>
    );
  return (
    <form className="subscribe-form" onSubmit={submit}>
      <label htmlFor="weekly-email">{copy.email}</label>
      <div>
        <input
          id="weekly-email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button className="button ink" disabled={state === "sending"}>
          {copy.subscribe}
        </button>
      </div>
      <label className="newsletter-consent">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>{copy.subscribeConsent}</span>
      </label>
      <label className="honeypot" aria-hidden="true">
        Website
        <input
          tabIndex={-1}
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
      </label>
      {state === "error" && (
        <p className="error" role="alert">
          {copy.newsletterError}
        </p>
      )}
    </form>
  );
}

function PrivateReflection({
  language,
  activeConversation = conversation,
}: {
  language: Language;
  activeConversation?: ConversationView;
}) {
  const copy = ui[language];
  const activePath = `/conversations/${activeConversation.slug}/`;
  const key = `heartspeaks:private:${activeConversation.slug}:${language}`;
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setText(localStorage.getItem(key) || "");
    setSaved(Boolean(localStorage.getItem(key)));
  }, [key]);
  const save = () => {
    localStorage.setItem(key, text);
    localStorage.setItem(
      "heartspeaks:last-reflection",
      activeConversation.slug,
    );
    setSaved(true);
    publicApi.track("private_reflection_saved", window.location.pathname);
  };
  return (
    <section id="private-reflection" className="private-reflection">
      <div className="privacy-seal">
        <i>{copy.privateSeal}</i>
        <span>{copy.privateOnly}</span>
      </div>
      <div>
        <p className="kicker">{copy.startReflection}</p>
        <h2>{copy.reflectionTitle}</h2>
        <p>{copy.reflectionBody}</p>
        <blockquote>{activeConversation[language].title}</blockquote>
        <textarea
          maxLength={2000}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setSaved(false);
          }}
          placeholder={copy.reflectionPlaceholder}
          aria-label={copy.reflectionTitle}
        />
        <div className="private-actions">
          <button
            className="button primary"
            disabled={!text.trim()}
            onClick={save}
          >
            {saved ? copy.savedPrivate : copy.savePrivate}
          </button>
          {saved && <a href={`${activePath}#share`}>{copy.continueShare} →</a>}
          <span>{text.length} / 2,000</span>
        </div>
      </div>
    </section>
  );
}

function HomeCollectionSection({
  number,
  label,
  title,
  body,
  href,
  items,
  language,
  emptyTitle,
  emptyBody,
}: {
  number: string;
  label: string;
  title: string;
  body: string;
  href: string;
  items: readonly LibraryItem[];
  language: Language;
  emptyTitle?: string;
  emptyBody?: string;
}) {
  return (
    <section className="home-editorial-section">
      <div className="home-section-heading">
        <div className="home-section-marker">
          <span>{number}</span>
          <p className="kicker">{label}</p>
        </div>
        <div>
          <h2>{title}</h2>
          <p>{body}</p>
        </div>
        <a className="quiet-link" href={href}>
          {ui[language].viewAll} →
        </a>
      </div>
      {items.length > 0 ? (
        <div className="library-grid home-editorial-grid">
          {items.map((item) => (
            <LibraryCard key={item.id} item={item} language={language} />
          ))}
        </div>
      ) : (
        <div className="home-section-empty">
          <h3>{emptyTitle}</h3>
          <p>{emptyBody}</p>
          <a href={href}>{ui[language].viewAll} →</a>
        </div>
      )}
    </section>
  );
}

function HomePage({
  language,
  onLanguage,
}: {
  language: Language;
  onLanguage: (language: Language) => void;
}) {
  const copy = ui[language];
  const conversations = libraryItems
    .filter((item) => item.type === "conversation")
    .slice(0, 3);
  const stories = libraryItems
    .filter((item) => item.type === "story")
    .slice(0, 3);
  const reflections = libraryItems
    .filter((item) => item.type === "reflection")
    .slice(0, 3);
  return (
    <main className="retention-home modern-home">
      <section className="library-hero">
        <div className="library-hero-copy">
          <h1>{copy.homeHeroTitle}</h1>
          <p>{copy.homeHeroBody}</p>
          <div className="home-hero-actions">
            <a className="button primary" href="/explore/">
              {copy.homePrimaryCta} <span aria-hidden="true">→</span>
            </a>
            <a className="home-secondary-link" href="#what-is-heartspeaks">
              {copy.homeSecondaryCta}
            </a>
          </div>
          <p className="home-trust-line">{copy.homeTrustLine}</p>
        </div>
        <aside
          className="home-question-panel"
          aria-label={copy.homePanelKicker}
        >
          <p className="kicker">{copy.homePanelKicker}</p>
          <div className="home-question-list">
            {copy.homePanelQuestions.map((question) => (
              <blockquote key={question}>“{question}”</blockquote>
            ))}
          </div>
          <p>{copy.homePanelTypes}</p>
        </aside>
      </section>

      <section id="what-is-heartspeaks" className="home-philosophy">
        <div className="home-section-marker">
          <span>01</span>
          <p className="kicker">{copy.homeAboutTitle}</p>
        </div>
        <div className="home-philosophy-copy">
          {copy.homeAboutStatement.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <a href="/about/">{copy.homeSecondaryCta} →</a>
        </div>
      </section>

      <section className="home-topics">
        <div className="home-section-heading">
          <div className="home-section-marker">
            <span>02</span>
            <p className="kicker">{copy.homeTopicsTitle}</p>
          </div>
          <div>
            <h2>{copy.homeTopicsTitle}</h2>
            <p>{copy.homeTopicsBody}</p>
          </div>
        </div>
        <div className="home-topic-grid">
          {copy.homeTopics.map((topic) => (
            <a key={topic} href={`/explore/?q=${encodeURIComponent(topic)}`}>
              <span>{topic}</span>
              <b aria-hidden="true">→</b>
            </a>
          ))}
        </div>
      </section>

      <HomeCollectionSection
        number="03"
        label={copy.nav[0]}
        title={copy.homeConversationsTitle}
        body={copy.homeConversationsBody}
        href="/conversations/"
        items={conversations}
        language={language}
      />

      <HomeCollectionSection
        number="04"
        label={copy.nav[1]}
        title={copy.homeStoriesTitle}
        body={copy.homeStoriesBody}
        href="/stories/"
        items={stories}
        language={language}
        emptyTitle={copy.noStoriesTitle}
        emptyBody={copy.noStoriesBody}
      />

      <HomeCollectionSection
        number="05"
        label={copy.nav[2]}
        title={copy.homeReflectionsTitle}
        body={copy.homeReflectionsBody}
        href="/reflections/"
        items={reflections}
        language={language}
      />

      <section className="home-bilingual">
        <div className="home-section-marker">
          <span>06</span>
          <p className="kicker">English + தமிழ்</p>
        </div>
        <div>
          <h2>{copy.homeBilingualTitle}</h2>
          <p>{copy.homeBilingualBody}</p>
        </div>
        <div className="home-language-actions" aria-label={copy.languageAria}>
          <button
            type="button"
            className={language === "en" ? "active" : ""}
            aria-pressed={language === "en"}
            onClick={() => onLanguage("en")}
          >
            {copy.readEnglish}
          </button>
          <span aria-hidden="true">|</span>
          <button
            type="button"
            className={language === "ta" ? "active" : ""}
            aria-pressed={language === "ta"}
            onClick={() => onLanguage("ta")}
          >
            {copy.readTamil}
          </button>
        </div>
      </section>

      {newsletterEnabled && (
        <section className="weekly-return">
          <div>
            <p className="kicker">{copy.newsletterKicker}</p>
            <h2>{copy.subscribeTitle}</h2>
            <p>{copy.subscribeBody}</p>
          </div>
          <NewsletterForm language={language} />
        </section>
      )}
    </main>
  );
}

function parseLibraryFilter(value: string | null): LibraryFilter {
  return value === "conversation" ||
    value === "story" ||
    value === "reflection" ||
    value === "question"
    ? value
    : "all";
}

function parseLibrarySearch(search: string) {
  const params = new URLSearchParams(search);
  return {
    query: params.get("q") || "",
    filter: parseLibraryFilter(params.get("type")),
  };
}

function ExplorePage({
  language,
  initialSearch = "",
}: {
  language: Language;
  initialSearch?: string;
}) {
  const copy = ui[language];
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<LibraryFilter>("all");
  const results = searchLibrary(query, filter);

  useEffect(() => {
    const initial = parseLibrarySearch(
      initialSearch ||
        (typeof window === "undefined" ? "" : window.location.search),
    );
    setQuery(initial.query);
    setFilter(initial.filter);
    const handlePopState = () => {
      const next = parseLibrarySearch(window.location.search);
      setQuery(next.query);
      setFilter(next.filter);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [initialSearch]);

  const updateUrl = (nextQuery: string, nextFilter: LibraryFilter) => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams();
    if (nextQuery.trim()) params.set("q", nextQuery.trim());
    if (nextFilter !== "all") params.set("type", nextFilter);
    const search = params.toString();
    window.history.replaceState(
      {},
      "",
      `/explore/${search ? `?${search}` : ""}`,
    );
  };

  const changeQuery = (value: string) => {
    setQuery(value);
    updateUrl(value, filter);
  };

  const changeFilter = (value: LibraryFilter) => {
    setFilter(value);
    updateUrl(query, value);
  };

  const reset = () => {
    setQuery("");
    setFilter("all");
    updateUrl("", "all");
  };

  const filters: { value: LibraryFilter; label: string }[] = [
    { value: "all", label: copy.allContent },
    { value: "conversation", label: copy.nav[0] },
    { value: "story", label: copy.nav[1] },
    ...(questionViews.length
      ? [{ value: "question" as const, label: questionCopy[language].nav }]
      : []),
    { value: "reflection", label: copy.nav[2] },
  ];

  return (
    <main className="explore-page">
      <header className="explore-intro">
        <p className="kicker">{copy.exploreKicker}</p>
        <h1>{copy.exploreTitle}</h1>
        <p>{copy.exploreBody}</p>
        <LibrarySearchForm
          language={language}
          value={query}
          onChange={changeQuery}
          onSubmit={(event) => {
            event.preventDefault();
            updateUrl(query, filter);
          }}
        />
      </header>
      <section className="explore-results" aria-label={copy.searchResults}>
        <div className="explore-toolbar">
          <div className="library-filters" aria-label={copy.exploreNav}>
            {filters.map((item) => (
              <button
                key={item.value}
                type="button"
                className={filter === item.value ? "active" : ""}
                aria-pressed={filter === item.value}
                onClick={() => changeFilter(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="results-summary" aria-live="polite">
            <strong>{results.length}</strong>{" "}
            <span>{results.length === 1 ? copy.result : copy.results}</span>
            {query && (
              <button type="button" onClick={() => changeQuery("")}>
                {copy.clearSearch}
              </button>
            )}
          </div>
        </div>
        {results.length > 0 ? (
          <div className="library-grid explore-grid">
            {results.map((item, index) => (
              <LibraryCard
                key={item.id}
                item={item}
                language={language}
                priority={index < 2}
              />
            ))}
          </div>
        ) : (
          <div className="library-empty">
            <h2>{copy.noResultsTitle}</h2>
            <p>{copy.noResultsBody}</p>
            <button className="button secondary" type="button" onClick={reset}>
              {copy.resetLibrary}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

function SubmissionForm({
  language,
  activeConversation,
}: {
  language: Language;
  activeConversation: ConversationView;
}) {
  const copy = activeConversation[language];
  const labels = ui[language];
  const [text, setText] = useState("");
  const [identityMode, setIdentityMode] = useState<IdentityMode>("anonymous");
  const [firstName, setFirstName] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<
    "idle" | "preview" | "sending" | "sent" | "error"
  >("idle");
  const piiWarning =
    /(?:\+?\d[\d\s()-]{7,}\d)|(?:[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i.test(
      text,
    );
  const submit = async () => {
    if (text.trim().length < 20 || (identityMode !== "private" && !consent))
      return;
    setState("sending");
    try {
      await publicApi.submit(activeConversation.slug, {
        text: text.trim(),
        language,
        identityMode,
        firstName: firstName.trim() || undefined,
        ageRange: ageRange || undefined,
        consent: identityMode === "private" ? false : consent,
        consentVersion: CONSENT_VERSION,
        website,
      });
      setState("sent");
      publicApi.track("submission_completed", window.location.pathname);
    } catch {
      setState("error");
    }
  };
  if (state === "sent")
    return (
      <div className="success-panel" role="status">
        <span className="success-mark">{labels.done}</span>
        <h3>{copy.success}</h3>
        <p>{labels.thanks}</p>
      </div>
    );
  return (
    <div className="form-shell">
      <div className="section-kicker">{labels.shareKicker}</div>
      <h2>{copy.formTitle}</h2>
      <p className="section-intro">{copy.formIntro}</p>
      {state === "preview" || state === "sending" ? (
        <div className="preview-card">
          <span>{labels.preview}</span>
          <blockquote>{text}</blockquote>
          <p>
            {identityMode === "anonymous"
              ? labels.anonymous
              : identityMode === "private"
                ? labels.privateIdentity
                : firstName || labels.firstName}
            {ageRange ? ` · ${ageRange}` : ""}
          </p>
          <div className="button-row">
            <button
              className="button secondary"
              onClick={() => setState("idle")}
              disabled={state === "sending"}
            >
              {labels.edit}
            </button>
            <button
              className="button primary"
              onClick={submit}
              disabled={
                state === "sending" || (identityMode !== "private" && !consent)
              }
            >
              {state === "sending" ? labels.sending : copy.submit}
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            setState("preview");
            publicApi.track("submission_previewed", window.location.pathname);
          }}
          onFocus={() =>
            publicApi.trackOnce("submission_started", window.location.pathname)
          }
        >
          <label htmlFor="story">{copy.storyLabel}</label>
          <textarea
            id="story"
            minLength={20}
            maxLength={4000}
            required
            value={text}
            onChange={(event) => setText(event.target.value)}
            placeholder={copy.storyPlaceholder}
          />
          <div className="character-count">{text.length} / 4,000</div>
          {piiWarning && <p className="privacy-warning">{labels.piiWarning}</p>}
          <label>{copy.identityLabel}</label>
          <div className="identity-grid">
            {(["anonymous", "first_name", "private"] as IdentityMode[]).map(
              (mode) => (
                <label
                  className={
                    identityMode === mode
                      ? "identity-option selected"
                      : "identity-option"
                  }
                  key={mode}
                >
                  <input
                    type="radio"
                    name="identity"
                    value={mode}
                    checked={identityMode === mode}
                    onChange={() => setIdentityMode(mode)}
                  />
                  <b>
                    {mode === "anonymous"
                      ? labels.anonymous
                      : mode === "first_name"
                        ? labels.firstName
                        : labels.privateIdentity}
                  </b>
                  <small>
                    {mode === "anonymous"
                      ? labels.anonymousHelp
                      : mode === "first_name"
                        ? labels.firstNameHelp
                        : labels.privateIdentityHelp}
                  </small>
                </label>
              ),
            )}
          </div>
          {identityMode === "first_name" && (
            <label>
              {labels.firstName}
              <input
                maxLength={60}
                required
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </label>
          )}
          <label>
            {labels.ageRange}{" "}
            <span className="optional">{labels.optional}</span>
            <select
              value={ageRange}
              onChange={(event) => setAgeRange(event.target.value)}
            >
              <option value="">{labels.preferNot}</option>
              <option>18–24</option>
              <option>25–34</option>
              <option>35–44</option>
              <option>45–59</option>
              <option>60+</option>
            </select>
          </label>
          <label className="honeypot" aria-hidden="true">
            Website
            <input
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
          </label>
          {identityMode !== "private" ? (
            <label className="consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(event) => setConsent(event.target.checked)}
                required
              />
              <span>{copy.consent}</span>
            </label>
          ) : (
            <p className="private-help">{copy.privateHelp}</p>
          )}
          <button
            className="button primary wide"
            type="submit"
            disabled={text.trim().length < 20}
          >
            {labels.previewResponse} <span>→</span>
          </button>
          {state === "error" && (
            <p className="error" role="alert">
              {labels.submissionError}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

function ExperienceSection({
  language,
  activeConversation,
}: {
  language: Language;
  activeConversation: ConversationView;
}) {
  const copy = activeConversation[language];
  const [items, setItems] = useState<Experience[]>([]);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">(
    "loading",
  );
  useEffect(() => {
    publicApi
      .experiences(activeConversation.slug)
      .then((result) => {
        setItems(result.items);
        setState("ready");
      })
      .catch(() => setState("unavailable"));
  }, [activeConversation.slug]);
  const compactState = state !== "ready" || items.length === 0;
  return (
    <section
      id="experiences"
      className={`published-experiences${compactState ? " compact-state" : ""}`}
    >
      <div className="conversation-heading">
        <p className="kicker">{ui[language].communityLabel}</p>
        <h2>{copy.experiencesTitle}</h2>
        <p>{copy.experiencesIntro}</p>
      </div>
      {state === "loading" ? (
        <p className="experience-empty compact">
          {ui[language].loadingExperiences}
        </p>
      ) : state === "unavailable" ? (
        <p className="experience-empty compact">{copy.unavailable}</p>
      ) : items.length === 0 ? (
        <div className="experience-empty compact">
          <p>{copy.empty}</p>
          <a className="button primary" href="#share">
            {ui[language].share}
          </a>
        </div>
      ) : (
        <div className="published-grid">
          {items.map((item) => (
            <article key={item.id}>
              <blockquote>{item.story}</blockquote>
              <p>
                {item.displayName}
                {item.ageRange ? ` · ${item.ageRange}` : ""}
              </p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ConversationPage({
  language,
  activeConversation,
}: {
  language: Language;
  activeConversation: ConversationView;
}) {
  const copy = activeConversation[language];
  const communityQuestion = questionViews.find(
    (question) =>
      question.source.type === "conversation" &&
      question.source.id === activeConversation.id,
  );
  const articleRef = useRef<HTMLElement>(null);
  const depthSent = useRef(new Set<number>());
  useEffect(() => {
    const measure = () => {
      const article = articleRef.current;
      if (!article) return;
      const progress = Math.min(
        100,
        Math.max(
          0,
          ((window.scrollY + window.innerHeight - article.offsetTop) /
            article.offsetHeight) *
            100,
        ),
      );
      [50, 90].forEach((depth) => {
        if (progress >= depth && !depthSent.current.has(depth)) {
          depthSent.current.add(depth);
          publicApi.track(`article_depth_${depth}`, window.location.pathname);
        }
      });
    };
    window.addEventListener("scroll", measure, { passive: true });
    measure();
    return () => window.removeEventListener("scroll", measure);
  }, []);
  return (
    <main className="conversation-page">
      <section
        className={`conversation-hero${activeConversation.heroLayout === "text" ? " without-media" : ""}`}
      >
        <div>
          <a href="/">← {ui[language].backThisWeek}</a>
          <p className="kicker">{ui[language].conversationLabel}</p>
          <h1>{copy.title}</h1>
          <p>{copy.intro}</p>
          <div className="button-row">
            <a className="button primary" href="#reflection">
              {ui[language].deeper} ↓
            </a>
            <ArticleShare
              language={language}
              title={copy.title}
              summary={copy.intro}
              published={activeConversation.status === "published"}
            />
          </div>
        </div>
        <ConversationMedia
          language={language}
          activeConversation={activeConversation}
          className="conversation-hero-media"
          sizes="(max-width: 900px) 100vw, 46vw"
          priority
        />
      </section>
      <article id="reflection" ref={articleRef} className="longform">
        <header>
          <p className="kicker">{ui[language].editorialReflection}</p>
          <h2>{copy.reflectionTitle}</h2>
          <p>
            {ui[language].originalEditorial} ·{" "}
            {activeConversation.readingMinutes} {ui[language].minRead}
          </p>
        </header>
        <div className="article-body">
          <div dangerouslySetInnerHTML={{ __html: copy.bodyHtml }} />
        </div>
      </article>
      {communityQuestion ? (
        <QuestionCard language={language} question={communityQuestion} />
      ) : (
        <PrivateReflection
          language={language}
          activeConversation={activeConversation}
        />
      )}
      <section className="prompt-interlude">
        <div>
          <p className="kicker">{ui[language].questionsWorth}</p>
          <h2>{copy.promptsTitle}</h2>
        </div>
        <ol>
          {copy.prompts.map((prompt, index) => (
            <li key={prompt}>
              <span>0{index + 1}</span>
              <p>{prompt}</p>
            </li>
          ))}
        </ol>
      </section>
      <section id="perspectives" className="conversation-perspectives">
        <div className="conversation-heading">
          <p className="kicker">{ui[language].compositesLabel}</p>
          <h2>{copy.perspectivesTitle}</h2>
          <p>{copy.perspectivesIntro}</p>
        </div>
        <div className="perspective-cards">
          {copy.perspectives.map((item, index) => (
            <article key={item.label}>
              <span>0{index + 1}</span>
              <blockquote>“{item.quote}”</blockquote>
              <p>{item.label}</p>
            </article>
          ))}
        </div>
        <small>{ui[language].compositeDisclaimer}</small>
      </section>
      {!communityQuestion && (
        <section className="article-share-closing conversation-share-closing">
          <p className="kicker">{ui[language].shareAgain}</p>
          <ArticleShare
            language={language}
            title={copy.title}
            summary={copy.intro}
            published={activeConversation.status === "published"}
          />
        </section>
      )}
      {publicApiEnabled && (
        <ExperienceSection
          language={language}
          activeConversation={activeConversation}
        />
      )}
      {publicApiEnabled && (
        <section id="share" className="share">
          <div className="share-note">
            <p>“{ui[language].sharePrinciple}”</p>
            <span>{ui[language].sharePrincipleLabel}</span>
            <ul>
              {ui[language].shareTrust.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
          <SubmissionForm
            language={language}
            activeConversation={activeConversation}
          />
        </section>
      )}
      {upcomingConversationPreviews[0] && (
        <section className="next-question">
          <p className="kicker">{ui[language].next}</p>
          <h2>{upcomingConversationPreviews[0].locales[language].title}</h2>
          <p>{ui[language].nextBody}</p>
          <a className="button secondary" href="/">
            {ui[language].returnDashboard}
          </a>
        </section>
      )}
    </main>
  );
}

function ConversationsIndex({ language }: { language: Language }) {
  const copy = pageCopy[language].conversations;
  const items = libraryItems.filter((item) => item.type === "conversation");
  return (
    <main className="collection-page conversations-index">
      <header className="collection-intro collection-intro-split">
        <div>
          <p className="kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
        </div>
        <p>{copy.intro}</p>
      </header>
      <section className="section-library" aria-label={copy.current}>
        <div className="section-library-heading">
          <p className="kicker">{copy.current}</p>
          <span>{items.length}</span>
        </div>
        <div className="library-grid section-library-grid">
          {items.map((item, index) => (
            <LibraryCard
              key={item.id}
              item={item}
              language={language}
              priority={index < 2}
            />
          ))}
        </div>
      </section>
      <section className="conversation-anatomy">
        <div>
          <p className="kicker">{copy.anatomy}</p>
          <h2>{ui[language].ritual}</h2>
        </div>
        <ol>
          {copy.anatomyItems.map((item, index) => (
            <li key={item.title}>
              <span aria-hidden="true">{index + 1}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      {upcomingConversationPreviews.length > 0 && (
        <section className="conversation-queue">
          <div className="section-title row">
            <div>
              <p className="kicker">{copy.next}</p>
              <h2>{ui[language].moreQuestions}</h2>
            </div>
            <div className="section-title-aside">
              <p>{copy.nextIntro}</p>
            </div>
          </div>
          <div className="upcoming-list">
            {upcomingConversationPreviews.map((item) => (
              <article key={item.id}>
                <div>
                  <span>{item.locales[language].topic}</span>
                  <span>{ui[language].comingSoon}</span>
                </div>
                <h3>{item.locales[language].title}</h3>
                <p>{item.locales[language].summary}</p>
              </article>
            ))}
          </div>
        </section>
      )}
      <aside className="collection-crosslink">
        <p className="kicker">{ui[language].storiesKicker}</p>
        <h2>{copy.storiesTitle}</h2>
        <p>{copy.storiesBody}</p>
        <a className="button secondary" href="/stories/">
          {copy.storiesLink} →
        </a>
      </aside>
    </main>
  );
}

function StoriesIndex({ language }: { language: Language }) {
  const copy = pageCopy[language].stories;
  const allStories = storyViews.filter((story) => story.status === "published");
  const [category, setCategory] = useState("all");
  const categories = [...new Set(allStories.map((story) => story.categoryKey))];
  const visibleStories =
    category === "all"
      ? allStories
      : allStories.filter((story) => story.categoryKey === category);
  const visibleItems = visibleStories
    .map((story) => libraryItems.find((item) => item.id === story.id))
    .filter((item): item is LibraryItem => Boolean(item));
  return (
    <main className="collection-page stories-index">
      <header className="collection-intro collection-intro-split">
        <div>
          <p className="kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
        </div>
        <p>{copy.intro}</p>
      </header>
      <section className="stories-collection" aria-label={copy.collection}>
        {allStories.length ? (
          <>
            {categories.length > 1 && (
              <div className="story-filters" aria-label={copy.collection}>
                <button
                  className={category === "all" ? "active" : ""}
                  type="button"
                  onClick={() => setCategory("all")}
                >
                  {language === "en" ? "All" : "அனைத்தும்"}
                </button>
                {categories.map((key) => {
                  const example = allStories.find(
                    (story) => story.categoryKey === key,
                  );
                  return (
                    <button
                      key={key}
                      className={category === key ? "active" : ""}
                      type="button"
                      onClick={() => setCategory(key)}
                    >
                      {example?.[language].category}
                    </button>
                  );
                })}
              </div>
            )}
            <div className="library-grid section-library-grid">
              {visibleItems.map((item, index) => (
                <LibraryCard
                  key={item.id}
                  item={item}
                  language={language}
                  priority={index === 0}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="stories-empty">
            <div>
              <p className="kicker">{copy.emptyKicker}</p>
              <h2>{ui[language].noStoriesTitle}</h2>
              <p>{ui[language].noStoriesBody}</p>
              <div className="hero-actions">
                {publicApiEnabled && (
                  <a
                    className="button primary"
                    href={`${conversationPath}#share`}
                  >
                    {ui[language].share}
                  </a>
                )}
                <a className="quiet-link" href="/editorial-guidelines/">
                  {ui[language].howStoriesPublished} →
                </a>
              </div>
            </div>
          </div>
        )}
      </section>
      <section className="story-types">
        <div className="section-title">
          <p className="kicker">{copy.typesTitle}</p>
          <h2>{ui[language].storiesTitle}</h2>
        </div>
        <ol>
          {copy.types.map((item) => (
            <li key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}

function StoryPage({
  language,
  story,
}: {
  language: Language;
  story: StoryView;
}) {
  const localized = story[language];
  const related = conversationViews.find(
    (item) => item.id === story.relatedConversationId,
  );
  return (
    <main className="story-page">
      <header className={`story-hero ${story.heroLayout}`}>
        <div>
          <a href="/stories/">← {ui[language].nav[1]}</a>
          <p className="kicker">
            {localized.category} · {storyTypeLabel(language, story)}
          </p>
          <h1>{localized.title}</h1>
          <p>{localized.summary}</p>
          <span>
            {localized.identityDisplay} · {story.readingMinutes}{" "}
            {ui[language].minRead}
          </span>
          <div className="story-hero-actions">
            <ArticleShare
              language={language}
              title={localized.title}
              summary={localized.summary}
              published={story.status === "published"}
            />
          </div>
        </div>
        <StoryMedia
          language={language}
          story={story}
          className="story-hero-media"
          sizes="(max-width: 900px) 100vw, 42vw"
          priority
        />
      </header>
      <article className="story-longform">
        <p className="kicker">{localized.kicker}</p>
        <div dangerouslySetInnerHTML={{ __html: localized.bodyHtml }} />
      </article>
      {localized.sourceNote && (
        <aside className="story-source-note">
          <p className="kicker">{ui[language].storySource}</p>
          <p>{localized.sourceNote}</p>
        </aside>
      )}
      <section className="story-closing-question">
        <p className="kicker">{localized.closingTitle}</p>
        <h2>{localized.closingQuestion}</h2>
        {related && (
          <div>
            <span>{ui[language].relatedConversation}</span>
            <a href={`/conversations/${related.slug}/`}>
              {related[language].title} →
            </a>
          </div>
        )}
        <div className="article-share-closing">
          <p className="kicker">{ui[language].shareAgain}</p>
          <ArticleShare
            language={language}
            title={localized.title}
            summary={localized.summary}
            published={story.status === "published"}
          />
        </div>
      </section>
    </main>
  );
}

function QuestionSubmissionForm({
  language,
  question,
  initialText,
}: {
  language: Language;
  question: QuestionView;
  initialText: string;
}) {
  const copy = questionCopy[language];
  const labels = ui[language];
  const [text, setText] = useState(initialText);
  const [identityMode, setIdentityMode] = useState<"anonymous" | "first_name">(
    "anonymous",
  );
  const [firstName, setFirstName] = useState("");
  const [ageRange, setAgeRange] = useState("");
  const [consent, setConsent] = useState(false);
  const [editorialReuseConsent, setEditorialReuseConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [state, setState] = useState<
    "idle" | "preview" | "sending" | "sent" | "error"
  >("idle");
  const piiWarning =
    /(?:\+?\d[\d\s()-]{7,}\d)|(?:[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/i.test(
      text,
    );
  const send = async () => {
    if (text.trim().length < 20 || !consent) return;
    setState("sending");
    try {
      await publicApi.submitQuestionReflection(question.slug, {
        text: text.trim(),
        language,
        identityMode,
        firstName: firstName.trim() || undefined,
        ageRange: ageRange || undefined,
        consent,
        consentVersion: CONSENT_VERSION,
        editorialReuseConsent,
        editorialReuseConsentVersion: editorialReuseConsent
          ? QUESTION_REUSE_CONSENT_VERSION
          : undefined,
        website,
      });
      setState("sent");
      publicApi.track(
        "question_reflection_submitted",
        window.location.pathname,
      );
    } catch {
      setState("error");
    }
  };
  if (state === "sent")
    return (
      <div className="question-success" role="status">
        <h3>{copy.successTitle}</h3>
        <p>{copy.successBody}</p>
      </div>
    );
  if (state === "preview" || state === "sending")
    return (
      <div className="question-preview">
        <p className="kicker">{copy.preview}</p>
        <blockquote>{text}</blockquote>
        <p>
          {identityMode === "anonymous" ? labels.anonymous : firstName}
          {ageRange ? " · " + ageRange : ""}
        </p>
        <div className="button-row">
          <button
            className="button secondary"
            type="button"
            disabled={state === "sending"}
            onClick={() => setState("idle")}
          >
            {labels.edit}
          </button>
          <button
            className="button primary"
            type="button"
            disabled={state === "sending" || !consent}
            onClick={send}
          >
            {state === "sending" ? copy.sending : copy.submit}
          </button>
        </div>
      </div>
    );
  return (
    <form
      className="question-submission-form"
      onSubmit={(event) => {
        event.preventDefault();
        setState("preview");
        publicApi.track(
          "question_reflection_previewed",
          window.location.pathname,
        );
      }}
      onFocus={() =>
        publicApi.trackOnce(
          "question_reflection_started",
          window.location.pathname,
        )
      }
    >
      <h3>{copy.shareTitle}</h3>
      <p>{copy.shareBody}</p>
      <label htmlFor={"question-reflection-" + question.id}>
        {question[language].question}
      </label>
      <textarea
        id={"question-reflection-" + question.id}
        minLength={20}
        maxLength={4000}
        required
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={copy.placeholder}
      />
      <div className="character-count">{text.length} / 4,000</div>
      {piiWarning && <p className="privacy-warning">{labels.piiWarning}</p>}
      <fieldset>
        <legend>{copy.identityLabel}</legend>
        <div className="identity-grid">
          {(["anonymous", "first_name"] as const).map((mode) => (
            <label
              className={
                identityMode === mode
                  ? "identity-option selected"
                  : "identity-option"
              }
              key={mode}
            >
              <input
                type="radio"
                name={"question-identity-" + question.id}
                checked={identityMode === mode}
                onChange={() => setIdentityMode(mode)}
              />
              <b>
                {mode === "anonymous" ? labels.anonymous : labels.firstName}
              </b>
              <small>
                {mode === "anonymous"
                  ? labels.anonymousHelp
                  : labels.firstNameHelp}
              </small>
            </label>
          ))}
        </div>
      </fieldset>
      {identityMode === "first_name" && (
        <label>
          {labels.firstName}
          <input
            maxLength={60}
            required
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </label>
      )}
      <label>
        {labels.ageRange} <span className="optional">{labels.optional}</span>
        <select
          value={ageRange}
          onChange={(event) => setAgeRange(event.target.value)}
        >
          <option value="">{labels.preferNot}</option>
          <option>18–24</option>
          <option>25–34</option>
          <option>35–44</option>
          <option>45–59</option>
          <option>60+</option>
        </select>
      </label>
      <label className="honeypot" aria-hidden="true">
        Website
        <input
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
        />
      </label>
      <label className="consent">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
        />
        <span>{copy.consent}</span>
      </label>
      <label className="consent">
        <input
          type="checkbox"
          checked={editorialReuseConsent}
          onChange={(event) => setEditorialReuseConsent(event.target.checked)}
        />
        <span>{copy.reuseConsent}</span>
      </label>
      <button
        className="button primary wide"
        type="submit"
        disabled={text.trim().length < 20 || !consent}
      >
        {copy.preview} <span>→</span>
      </button>
      {state === "error" && (
        <p className="error" role="alert">
          {copy.error}
        </p>
      )}
    </form>
  );
}

function QuestionCard({
  language,
  question,
}: {
  language: Language;
  question: QuestionView;
}) {
  const copy = questionCopy[language];
  const storageKey =
    "heartspeaks:question:private:" + question.id + ":" + language;
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  const [sharing, setSharing] = useState(false);
  useEffect(() => {
    const existing = localStorage.getItem(storageKey) || "";
    setText(existing);
    setSaved(Boolean(existing));
    setSharing(false);
  }, [storageKey]);
  const save = () => {
    localStorage.setItem(storageKey, text);
    setSaved(true);
    publicApi.track("question_private_saved", window.location.pathname);
  };
  const remove = () => {
    localStorage.removeItem(storageKey);
    setText("");
    setSaved(false);
    setSharing(false);
  };
  return (
    <section className="question-card" id={"question-" + question.slug}>
      <p className="kicker">{copy.questionKicker}</p>
      <h2>{question[language].question}</h2>
      {!sharing ? (
        <>
          <div className="question-private-intro">
            <h3>{copy.privateTitle}</h3>
            <p>{copy.privateBody}</p>
          </div>
          <textarea
            maxLength={2000}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setSaved(false);
            }}
            placeholder={copy.placeholder}
            aria-label={copy.privateTitle}
          />
          <div className="question-card-actions">
            <button
              className="button primary"
              type="button"
              disabled={!text.trim()}
              onClick={save}
            >
              {saved ? copy.savedPrivate : copy.savePrivate}
            </button>
            <button
              className="button secondary"
              type="button"
              disabled={!text.trim()}
              onClick={() => setSharing(true)}
            >
              {copy.share}
            </button>
            {saved && (
              <button className="text-button" type="button" onClick={remove}>
                {copy.deletePrivate}
              </button>
            )}
            <span>{text.length} / 2,000</span>
          </div>
        </>
      ) : (
        <QuestionSubmissionForm
          language={language}
          question={question}
          initialText={text}
        />
      )}
    </section>
  );
}

function QuestionVoices({
  language,
  question,
}: {
  language: Language;
  question: QuestionView;
}) {
  const copy = questionCopy[language];
  const [items, setItems] = useState<QuestionVoice[]>([]);
  const [count, setCount] = useState(0);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">(
    "loading",
  );
  const [reporting, setReporting] = useState<string>();
  const [reported, setReported] = useState<string>();
  const [reportError, setReportError] = useState<string>();
  useEffect(() => {
    publicApi
      .questionReflections(question.slug)
      .then((result) => {
        setItems(result.items);
        setCount(result.count);
        setState("ready");
        if (result.items.length)
          publicApi.trackOnce(
            "question_voices_viewed",
            window.location.pathname,
          );
      })
      .catch(() => setState("unavailable"));
  }, [question.slug]);
  const submitReport = async (
    reflectionId: string,
    reason: "privacy" | "harm" | "other",
  ) => {
    try {
      await publicApi.reportQuestionReflection(
        question.slug,
        reflectionId,
        reason,
      );
      setReported(reflectionId);
      setReporting(undefined);
      setReportError(undefined);
    } catch {
      setReportError(reflectionId);
    }
  };
  return (
    <section className="question-voices" id="reader-voices">
      <p className="kicker">{copy.voices}</p>
      <h2>{copy.voices}</h2>
      <p>{copy.voicesBody}</p>
      {state === "loading" ? (
        <p className="experience-empty compact">{copy.voicesLoading}</p>
      ) : state === "unavailable" ? (
        <p className="experience-empty compact">{copy.voicesUnavailable}</p>
      ) : items.length === 0 ? (
        <p className="experience-empty compact">{copy.voicesEmpty}</p>
      ) : (
        <>
          <p className="question-voice-count">
            {count} {count === 1 ? copy.countOne : copy.countMany}
          </p>
          <div className="question-voice-grid">
            {items.map((item) => (
              <article key={item.id}>
                <blockquote>{item.story}</blockquote>
                <p>
                  {item.displayName}
                  {item.ageRange ? " · " + item.ageRange : ""}
                  {item.language !== language
                    ? " · " + item.language.toUpperCase()
                    : ""}
                </p>
                {reported === item.id ? (
                  <p className="question-report-status" role="status">
                    {copy.reportSent}
                  </p>
                ) : reporting === item.id ? (
                  <div className="question-report-options">
                    <span>{copy.reportPrompt}</span>
                    <button
                      type="button"
                      onClick={() => void submitReport(item.id, "privacy")}
                    >
                      {copy.reportPrivacy}
                    </button>
                    <button
                      type="button"
                      onClick={() => void submitReport(item.id, "harm")}
                    >
                      {copy.reportHarm}
                    </button>
                    <button
                      type="button"
                      onClick={() => void submitReport(item.id, "other")}
                    >
                      {copy.reportOther}
                    </button>
                  </div>
                ) : publicApiEnabled ? (
                  <button
                    className="text-button"
                    type="button"
                    onClick={() => setReporting(item.id)}
                  >
                    {copy.report}
                  </button>
                ) : null}
                {reportError === item.id && (
                  <p className="error" role="alert">
                    {copy.reportError}
                  </p>
                )}
              </article>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function QuestionSourceLink({
  language,
  question,
}: {
  language: Language;
  question: QuestionView;
}) {
  const copy = questionCopy[language];
  const source =
    question.source.type === "conversation"
      ? conversationViews.find((item) => item.id === question.source.id)
      : reflectionViews.find((item) => item.id === question.source.id);
  if (!source) return null;
  const href =
    question.source.type === "conversation"
      ? "/conversations/" + source.slug + "/"
      : "/reflections/" + source.slug + "/";
  return (
    <aside className="question-source">
      <p className="kicker">{copy.source}</p>
      <h2>{source[language].title}</h2>
      <a href={href}>
        {question.source.type === "conversation"
          ? copy.readConversation
          : copy.readSource}{" "}
        →
      </a>
    </aside>
  );
}

function QuestionsIndex({ language }: { language: Language }) {
  const copy = questionCopy[language];
  return (
    <main className="collection-page questions-index">
      <header className="collection-intro collection-intro-split">
        <div>
          <p className="kicker">{copy.indexKicker}</p>
          <h1>{copy.indexTitle}</h1>
        </div>
        <p>{copy.indexBody}</p>
      </header>
      {questionViews.length ? (
        <section className="question-library">
          {questionViews.map((question) => (
            <article key={question.id}>
              <p className="kicker">{question.topic}</p>
              <h2>{question[language].question}</h2>
              <p>{question[language].summary}</p>
              <div className="button-row">
                <a
                  className="button primary"
                  href={"/questions/" + question.slug + "/"}
                >
                  {copy.reflect}
                </a>
                <a
                  className="button secondary"
                  href={"/questions/" + question.slug + "/#reader-voices"}
                >
                  {copy.readVoices}
                </a>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="question-empty">
          <h2>{copy.emptyTitle}</h2>
          <p>{copy.emptyBody}</p>
        </section>
      )}
    </main>
  );
}

function QuestionPage({
  language,
  question,
}: {
  language: Language;
  question: QuestionView;
}) {
  const copy = questionCopy[language];
  return (
    <main className="question-page">
      <header className="question-hero">
        <a href="/questions/">← {copy.nav}</a>
        <p className="kicker">{question.topic}</p>
        <h1>{question[language].question}</h1>
        <p>{question[language].summary}</p>
      </header>
      <QuestionCard language={language} question={question} />
      <QuestionVoices language={language} question={question} />
      <QuestionSourceLink language={language} question={question} />
    </main>
  );
}

function ReflectionsIndex({ language }: { language: Language }) {
  const copy = pageCopy[language].reflections;
  const available = libraryItems.filter((item) => item.type === "reflection");
  return (
    <main className="collection-page reflections-index">
      <header className="collection-intro collection-intro-split">
        <div>
          <p className="kicker">{copy.kicker}</p>
          <h1>{copy.title}</h1>
        </div>
        <p>{copy.intro}</p>
      </header>
      <section className="reflections-collection" aria-label={copy.collection}>
        {available.length ? (
          <div className="library-grid section-library-grid">
            {available.map((item, index) => (
              <LibraryCard
                key={item.id}
                item={item}
                language={language}
                priority={index < 2}
              />
            ))}
          </div>
        ) : (
          <div className="stories-empty">
            <div>
              <p className="kicker">{copy.collection}</p>
              <h2>{copy.emptyTitle}</h2>
              <p>{copy.emptyBody}</p>
            </div>
          </div>
        )}
      </section>
      <aside className="reflection-method">
        <p className="kicker">{copy.source}</p>
        <h2>{copy.methodTitle}</h2>
        <p>{copy.methodBody}</p>
      </aside>
    </main>
  );
}

function prepareReflectionSections(bodyHtml: string) {
  const headingPattern = /<h2>([\s\S]*?)<\/h2>/g;
  const matches = [...bodyHtml.matchAll(headingPattern)];
  const openingHtml = matches.length
    ? bodyHtml.slice(0, matches[0].index)
    : bodyHtml;
  const sections = matches.map((match, index) => {
    const contentStart = (match.index || 0) + match[0].length;
    const contentEnd = matches[index + 1]?.index ?? bodyHtml.length;
    return {
      id: `reflection-section-${index + 1}`,
      titleHtml: match[1],
      contentHtml: bodyHtml.slice(contentStart, contentEnd),
    };
  });
  return { openingHtml, sections };
}

type ReflectionSection = ReturnType<
  typeof prepareReflectionSections
>["sections"][number];

function ReflectionReadingNavigator({
  slug,
  title,
  topic,
  sections,
  copy,
}: {
  slug: string;
  title: string;
  topic: string;
  sections: ReflectionSection[];
  copy: (typeof pageCopy)[Language]["reflections"];
}) {
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [tocExpanded, setTocExpanded] = useState(true);
  const [mobileContentsOpen, setMobileContentsOpen] = useState(false);
  const sheetCloseButton = useRef<HTMLButtonElement>(null);
  const tocPreferenceKey = `heartspeaks:reflection-toc:${slug}`;

  useEffect(() => {
    setTocExpanded(sessionStorage.getItem(tocPreferenceKey) !== "collapsed");
  }, [tocPreferenceKey]);

  useEffect(() => {
    const update = () => {
      const article = document.getElementById("reflection-article");
      const hero = document.querySelector<HTMLElement>(".reflection-hero");
      if (!article || !hero) return;
      const articleTop = window.scrollY + article.getBoundingClientRect().top;
      const readableHeight = Math.max(
        article.offsetHeight - window.innerHeight,
        1,
      );
      const next = Math.min(
        100,
        Math.max(0, ((window.scrollY - articleTop) / readableHeight) * 100),
      );
      setProgress(Math.round(next));

      const stickyOffset = window.innerWidth <= 760 ? 138 : 162;
      let current = 0;
      sections.forEach((section, index) => {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top <= stickyOffset) {
          current = index;
        }
      });
      setActiveIndex(current);
      setHeaderVisible(
        hero.getBoundingClientRect().bottom <=
          (window.innerWidth <= 760 ? 64 : 72) &&
          article.getBoundingClientRect().bottom > stickyOffset,
      );
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [sections]);

  useEffect(() => {
    if (!mobileContentsOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    sheetCloseButton.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileContentsOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [mobileContentsOpen]);

  const links = sections.map((section, index) => (
    <li key={section.id} className={index === activeIndex ? "active" : ""}>
      <a
        href={`#${section.id}`}
        aria-current={index === activeIndex ? "location" : undefined}
        onClick={() => setMobileContentsOpen(false)}
      >
        <span>{String(index + 1).padStart(2, "0")}</span>
        <span dangerouslySetInnerHTML={{ __html: section.titleHtml }} />
      </a>
    </li>
  ));

  return (
    <>
      <header
        className={`reflection-sticky-header ${headerVisible ? "visible" : ""}`}
        aria-hidden={!headerVisible}
        inert={!headerVisible}
      >
        <div className="reflection-sticky-header-inner">
          <a className="reflection-sticky-back" href="/reflections/">
            ← <span>{copy.reflectionLabel}</span>
          </a>
          <div className="reflection-sticky-context">
            <strong>{title}</strong>
            <span>
              {String(activeIndex + 1).padStart(2, "0")} /{" "}
              {String(sections.length).padStart(2, "0")} ·{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: sections[activeIndex]?.titleHtml || topic,
                }}
              />
            </span>
          </div>
          <button
            className="reflection-mobile-contents-trigger"
            type="button"
            aria-expanded={mobileContentsOpen}
            aria-controls="reflection-mobile-contents"
            onClick={() => setMobileContentsOpen(true)}
          >
            <span>{copy.contents}</span> {progress}%
          </button>
        </div>
        <progress
          className="reflection-header-progress"
          aria-label={copy.readingProgress}
          max={100}
          value={progress}
        />
      </header>

      <aside
        className={`reflection-reader-rail ${headerVisible ? "visible" : ""} ${tocExpanded ? "expanded" : "collapsed"}`}
        inert={!headerVisible}
      >
        <div className="reflection-rail-heading">
          <p className="kicker">{copy.tocLabel}</p>
          <button
            type="button"
            aria-label={
              tocExpanded ? copy.collapseContents : copy.expandContents
            }
            aria-expanded={tocExpanded}
            onClick={() => {
              setTocExpanded((expanded) => {
                const next = !expanded;
                sessionStorage.setItem(
                  tocPreferenceKey,
                  next ? "expanded" : "collapsed",
                );
                return next;
              });
            }}
          >
            {tocExpanded ? "‹" : "›"}
          </button>
        </div>
        <ol>{links}</ol>
      </aside>

      {mobileContentsOpen && (
        <div
          className="reflection-sheet-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget)
              setMobileContentsOpen(false);
          }}
        >
          <section
            id="reflection-mobile-contents"
            className="reflection-contents-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reflection-contents-title"
          >
            <header>
              <div>
                <p id="reflection-contents-title">{copy.readingGuide}</p>
                <span>
                  {copy.readingProgress} · {progress}%
                </span>
              </div>
              <button
                ref={sheetCloseButton}
                type="button"
                aria-label={copy.closeContents}
                onClick={() => setMobileContentsOpen(false)}
              >
                ×
              </button>
            </header>
            <ol>{links}</ol>
          </section>
        </div>
      )}
    </>
  );
}

function ReflectionHeart({
  language,
  reflection,
}: {
  language: Language;
  reflection: ReflectionView;
}) {
  const copy = pageCopy[language].reflections;
  const storageKey = `heartspeaks:reflection-heart:${reflection.slug}`;
  const pendingKey = `${storageKey}:pending`;
  const [hearted, setHearted] = useState(false);
  const [count, setCount] = useState(0);
  const [state, setState] = useState<"idle" | "saving" | "failed">("idle");
  const [pendingAction, setPendingAction] = useState<
    { action: "like" | "unlike"; operationId: string } | undefined
  >();

  const saveHeart = useCallback(
    (pending: { action: "like" | "unlike"; operationId: string }) => {
      const nextHearted = pending.action === "like";
      setPendingAction(pending);
      setHearted(nextHearted);
      setState("saving");
      if (!publicApiEnabled) {
        if (nextHearted) localStorage.setItem(storageKey, "true");
        else localStorage.removeItem(storageKey);
        sessionStorage.removeItem(pendingKey);
        setPendingAction(undefined);
        setState("idle");
        return;
      }

      void publicApi
        .setReflectionHeart(
          reflection.slug,
          pending.action,
          pending.operationId,
        )
        .then(({ count: nextCount }) => {
          setCount(nextCount);
          if (nextHearted) localStorage.setItem(storageKey, "true");
          else localStorage.removeItem(storageKey);
          sessionStorage.removeItem(pendingKey);
          setPendingAction(undefined);
          setState("idle");
        })
        .catch(() => setState("failed"));
    },
    [pendingKey, reflection.slug, storageKey],
  );

  useEffect(() => {
    setHearted(localStorage.getItem(storageKey) === "true");
    const storedPending = sessionStorage.getItem(pendingKey);
    if (!storedPending) return;
    try {
      const pending = JSON.parse(storedPending) as {
        action?: string;
        operationId?: string;
      };
      if (
        (pending.action === "like" || pending.action === "unlike") &&
        typeof pending.operationId === "string"
      ) {
        saveHeart(
          pending as { action: "like" | "unlike"; operationId: string },
        );
        return;
      }
    } catch {
      // Ignore an invalid local value and let the reader try again.
    }
    sessionStorage.removeItem(pendingKey);
  }, [pendingKey, saveHeart, storageKey]);

  useEffect(() => {
    let active = true;
    if (publicApiEnabled) {
      void publicApi
        .reflectionHeartCount(reflection.slug)
        .then(({ count: nextCount }) => {
          if (active) setCount(nextCount);
        })
        .catch(() => undefined);
    }
    return () => {
      active = false;
    };
  }, [reflection.slug]);

  const toggleHeart = () => {
    if (state === "saving") return;
    const pending =
      state === "failed" && pendingAction
        ? pendingAction
        : {
            action: hearted ? ("unlike" as const) : ("like" as const),
            operationId: crypto.randomUUID(),
          };
    sessionStorage.setItem(pendingKey, JSON.stringify(pending));
    saveHeart(pending);
  };

  const status =
    state === "saving"
      ? copy.heartSaving
      : state === "failed"
        ? copy.heartError
        : hearted
          ? copy.hearted
          : copy.heartPrompt;
  const buttonLabel =
    state === "saving"
      ? copy.heartSaving
      : state === "failed"
        ? copy.heartRetry
        : hearted
          ? copy.heartRemove
          : copy.heart;
  const countLabel = `${count} ${count === 1 ? copy.heartOne : copy.heartMany}`;
  const countId = `reflection-heart-count-${reflection.slug}`;

  return (
    <div className="reflection-heart">
      <p className="reflection-heart-status" aria-live="polite">
        {status}
      </p>
      <button
        className={hearted ? "hearted" : ""}
        type="button"
        aria-label={buttonLabel}
        aria-pressed={hearted}
        aria-describedby={countId}
        disabled={state === "saving"}
        onClick={toggleHeart}
      >
        <span className="reflection-heart-icon" aria-hidden="true">
          {hearted ? "♥" : "♡"}
        </span>
        <span className="reflection-heart-count" aria-hidden="true">
          {count}
        </span>
      </button>
      <span id={countId} className="visually-hidden">
        {countLabel}
      </span>
      {state === "failed" && <p className="reflection-heart-error">{status}</p>}
    </div>
  );
}

function ReflectionPage({
  language,
  reflection,
}: {
  language: Language;
  reflection: ReflectionView;
}) {
  const copy = pageCopy[language].reflections;
  const localized = reflection[language];
  const communityQuestion = questionViews.find(
    (question) =>
      question.source.type === "reflection" &&
      question.source.id === reflection.id,
  );
  const related = conversationViews.filter((item) =>
    reflection.relatedConversationIds.includes(item.id),
  );
  const reading = prepareReflectionSections(localized.bodyHtml);
  return (
    <main id="reflection-top" className="reflection-page">
      <ReflectionReadingNavigator
        slug={reflection.slug}
        title={localized.title}
        topic={localized.topic}
        sections={reading.sections}
        copy={copy}
      />
      <header className={`reflection-hero ${reflection.heroLayout}`}>
        <div>
          <a href="/reflections/">← {ui[language].nav[2]}</a>
          <p className="kicker">
            {localized.kicker} · {localized.topic}
          </p>
          <h1>{localized.title}</h1>
          <p>{localized.summary}</p>
          <span>
            {reflection.readingMinutes} {ui[language].minRead} ·{" "}
            {ui[language].bilingual}
          </span>
          {(reflection.series || reflection.tags.length > 0) && (
            <div className="reflection-taxonomy" aria-label={copy.metadata}>
              {reflection.series && (
                <a
                  className="reflection-series"
                  href={reflectionExploreHref(reflection.series, "reflection")}
                >
                  <span>{copy.series}</span>
                  {displayReflectionMetadata(language, reflection.series)}
                </a>
              )}
              {reflection.tags.length > 0 && (
                <div className="reflection-tags">
                  <span className="reflection-taxonomy-label">
                    {copy.topics}
                  </span>
                  {reflection.tags.map((tag) => (
                    <a key={tag} href={reflectionExploreHref(tag)}>
                      {displayReflectionMetadata(language, tag)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
          {reflection.status === "approved" && <small>{copy.approved}</small>}
          <div className="reflection-actions">
            <a className="reflection-begin" href="#reflection-article">
              {copy.beginReading} ↓
            </a>
            <ArticleShare
              language={language}
              title={localized.title}
              summary={localized.summary}
              published={reflection.status === "published"}
            />
          </div>
        </div>
        {reflection.heroImage && localized.imageAlt && (
          <figure className="reflection-hero-media">
            <EditorialImage
              image={reflection.heroImage}
              alt={localized.imageAlt}
              sizes="(max-width: 900px) 100vw, 42vw"
              priority
            />
            {reflection.heroMedia && (
              <figcaption>{reflection.heroMedia.credit}</figcaption>
            )}
          </figure>
        )}
      </header>
      <div className="reflection-reader">
        <div className="reflection-rail-space" aria-hidden="true" />
        <article id="reflection-article" className="reflection-longform">
          <div
            className="reflection-opening"
            dangerouslySetInnerHTML={{ __html: reading.openingHtml }}
          />
          {reading.sections.map((section, index) => {
            const isPrompts = section.titleHtml === localized.promptsTitle;
            const isSource = index === reading.sections.length - 1;
            return (
              <section
                className={`${isPrompts ? "prompts" : ""} ${isSource ? "source" : ""}`.trim()}
                id={section.id}
                key={section.id}
              >
                <span className="reflection-section-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h2 dangerouslySetInnerHTML={{ __html: section.titleHtml }} />
                <div
                  dangerouslySetInnerHTML={{ __html: section.contentHtml }}
                />
              </section>
            );
          })}
          {communityQuestion ? (
            <QuestionCard language={language} question={communityQuestion} />
          ) : (
            <section className="reflection-closing-question">
              <p className="kicker">{localized.closingTitle}</p>
              <h2>{localized.closingQuestion}</h2>
              <ArticleShare
                language={language}
                title={localized.title}
                summary={localized.summary}
                published={reflection.status === "published"}
              />
            </section>
          )}
          <ReflectionHeart language={language} reflection={reflection} />
          <div className="reflection-back-top">
            <button
              type="button"
              onClick={() =>
                window.scrollTo({
                  top: 0,
                  behavior: window.matchMedia?.(
                    "(prefers-reduced-motion: reduce)",
                  ).matches
                    ? "auto"
                    : "smooth",
                })
              }
            >
              {copy.backToTop} <span aria-hidden="true">↑</span>
            </button>
          </div>
        </article>
      </div>
      {related.length > 0 && (
        <section className="reflection-related">
          <p className="kicker">{ui[language].relatedConversation}</p>
          {related.map((item) => (
            <a key={item.id} href={`/conversations/${item.slug}/`}>
              {item[language].title} →
            </a>
          ))}
        </section>
      )}
    </main>
  );
}

type InformationContent = {
  kicker: string;
  title: string;
  lead?: string;
  updated?: string;
  sections: readonly { title: string; body: string }[];
};

function InformationPage({ content }: { content: InformationContent }) {
  return (
    <main className="information-page">
      <header>
        <p className="kicker">{content.kicker}</p>
        <h1>{content.title}</h1>
        {content.lead && <p className="information-lead">{content.lead}</p>}
        {content.updated && (
          <p className="information-updated">{content.updated}</p>
        )}
      </header>
      <div className="information-sections">
        {content.sections.map((section, index) => (
          <section key={section.title}>
            <span>0{index + 1}</span>
            <div>
              <h2>{section.title}</h2>
              <p>{section.body}</p>
            </div>
          </section>
        ))}
      </div>
    </main>
  );
}

function NotFound({
  language,
  kind = "conversation",
}: {
  language: Language;
  kind?: "conversation" | "story" | "reflection" | "question";
}) {
  const copy = ui[language];
  const questionLabels = questionCopy[language];
  const isStory = kind === "story";
  const isReflection = kind === "reflection";
  const isQuestion = kind === "question";
  const title = isStory
    ? copy.storyNotFoundTitle
    : isQuestion
      ? questionLabels.questionNotFoundTitle
      : isReflection
        ? copy.reflectionNotFoundTitle
        : copy.notFoundTitle;
  const body = isStory
    ? copy.storyNotFoundBody
    : isQuestion
      ? questionLabels.questionNotFoundBody
      : isReflection
        ? copy.reflectionNotFoundBody
        : copy.notFoundBody;
  const href = isStory
    ? "/stories/"
    : isQuestion
      ? "/questions/"
      : isReflection
        ? "/reflections/"
        : "/";
  const label = isStory
    ? copy.storyNotFoundReturn
    : isQuestion
      ? questionLabels.questionNotFoundReturn
      : isReflection
        ? copy.reflectionNotFoundReturn
        : copy.notFoundReturn;
  return (
    <main className="not-found">
      <p className="kicker">404 · HeartSpeaks</p>
      <h1>{title}</h1>
      <p>{body}</p>
      <a className="button primary" href={href}>
        {label}
      </a>
    </main>
  );
}

export function PublicSite({
  initialPath,
  initialSearch,
  initialLanguage = "en",
}: {
  initialPath?: string;
  initialSearch?: string;
  initialLanguage?: Language;
}) {
  const rawPath =
    initialPath ??
    (typeof window === "undefined" ? "/" : window.location.pathname);
  const rawPathname = rawPath.split(/[?#]/, 1)[0] || "/";
  const localizedLanguage = /^\/ta(?:\/|$)/.test(rawPathname)
    ? "ta"
    : undefined;
  const path =
    (localizedLanguage
      ? rawPathname.replace(/^\/ta(?=\/|$)/, "")
      : rawPathname) || "/";
  const search =
    initialSearch ??
    (rawPath.includes("?")
      ? `?${rawPath.split("?")[1]?.split("#")[0] || ""}`
      : typeof window === "undefined"
        ? ""
        : window.location.search);
  const [language, setLanguage] = useState<Language>(
    localizedLanguage || initialLanguage,
  );
  useEffect(() => {
    if (localizedLanguage) {
      setLanguage(localizedLanguage);
      return;
    }
    const saved = localStorage.getItem(
      "heartspeaks-language",
    ) as Language | null;
    if (saved === "en" || saved === "ta") setLanguage(saved);
    const visits = Number(localStorage.getItem("heartspeaks:visits") || "0");
    localStorage.setItem("heartspeaks:visits", String(visits + 1));
    publicApi.track(visits > 0 ? "return_visit" : "first_visit", path);
  }, [path, localizedLanguage]);
  useEffect(() => {
    localStorage.setItem("heartspeaks-language", language);
    document.documentElement.lang = language;
  }, [language]);
  const changeLanguage = (value: Language) => {
    if (typeof window !== "undefined") {
      const nextPath = value === "ta" ? `/ta${path}` : path;
      window.history.pushState({}, "", nextPath === "/ta/" ? "/ta/" : nextPath);
    }
    setLanguage(value);
    publicApi.track("language_changed", path);
  };
  const conversationMatch = path.match(/^\/conversations\/([a-z0-9-]+)\/?$/);
  const storyMatch = path.match(/^\/stories\/([a-z0-9-]+)\/?$/);
  const reflectionMatch = path.match(/^\/reflections\/([a-z0-9-]+)\/?$/);
  const questionMatch = path.match(/^\/questions\/([a-z0-9-]+)\/?$/);
  const normalizedPath = path === "/" ? "/" : path.replace(/\/$/, "");
  const isConversationPath = normalizedPath.startsWith("/conversations/");
  const isStoryPath = normalizedPath.startsWith("/stories/");
  const isReflectionPath = normalizedPath.startsWith("/reflections/");
  const isQuestionPath = normalizedPath.startsWith("/questions/");
  const activeConversation = conversationMatch
    ? getConversationView(conversationMatch[1])
    : undefined;
  const activeStory = storyMatch ? getStoryView(storyMatch[1]) : undefined;
  const activeReflection = reflectionMatch
    ? getReflectionView(reflectionMatch[1])
    : undefined;
  const activeQuestion = questionMatch
    ? getQuestionView(questionMatch[1])
    : undefined;
  return (
    <div className="retention-site" lang={language}>
      <a className="skip-link" href="#main-content">
        {ui[language].skip}
      </a>
      <Header language={language} onLanguage={changeLanguage} path={path} />
      <div id="main-content">
        {normalizedPath === "/explore" ? (
          <ExplorePage language={language} initialSearch={search} />
        ) : normalizedPath === "/conversations" ? (
          <ConversationsIndex language={language} />
        ) : normalizedPath === "/stories" ? (
          <StoriesIndex language={language} />
        ) : normalizedPath === "/reflections" ? (
          <ReflectionsIndex language={language} />
        ) : normalizedPath === "/questions" ? (
          <QuestionsIndex language={language} />
        ) : normalizedPath === "/about" ? (
          <InformationPage content={pageCopy[language].about} />
        ) : normalizedPath === "/privacy" ? (
          <InformationPage content={pageCopy[language].privacyPage} />
        ) : normalizedPath === "/editorial-guidelines" ? (
          <InformationPage content={pageCopy[language].guidelinesPage} />
        ) : activeConversation ? (
          <ConversationPage
            language={language}
            activeConversation={activeConversation}
          />
        ) : isConversationPath ? (
          <NotFound language={language} />
        ) : activeStory ? (
          <StoryPage language={language} story={activeStory} />
        ) : isStoryPath ? (
          <NotFound language={language} kind="story" />
        ) : activeReflection ? (
          <ReflectionPage language={language} reflection={activeReflection} />
        ) : isReflectionPath ? (
          <NotFound language={language} kind="reflection" />
        ) : activeQuestion ? (
          <QuestionPage language={language} question={activeQuestion} />
        ) : isQuestionPath ? (
          <NotFound language={language} kind="question" />
        ) : normalizedPath === "/" ? (
          <HomePage language={language} onLanguage={changeLanguage} />
        ) : (
          <NotFound language={language} />
        )}
      </div>
      <Footer language={language} />
    </div>
  );
}
